// Use self-resolving ESM URLs so this module does not depend on a theme-level
// import map. Shopify may execute other module scripts before a section loads,
// which can prevent a section-local import map from being applied.
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.185.1/+esm';
import { OrbitControls } from 'https://cdn.jsdelivr.net/npm/three@0.185.1/examples/jsm/controls/OrbitControls.js/+esm';
import { RoundedBoxGeometry } from 'https://cdn.jsdelivr.net/npm/three@0.185.1/examples/jsm/geometries/RoundedBoxGeometry.js/+esm';

const mount = document.getElementById('gebblo-3d');
const plannerRoot = document.querySelector('.gebblo-planner');
const storefrontToken = plannerRoot.dataset.storefrontToken;
const plannerCheckoutEnabled = plannerRoot.dataset.plannerCheckoutEnabled === 'true';
const gBlockLogoUrl = plannerRoot.dataset.gblockLogo;
const gCurveLogoUrl = plannerRoot.dataset.gcurveLogo;
let selectedFrameProduct = null;
const GCURVE = {
  width: 480,
  height: 280,
  depth: 220,
  screenSideDepth: 107,
  screenCentreDepth: 190,
  minimumRoom: { width: 500, height: 300, depth: 550 }
};
let selectedVariant = null;
let roomFitNotice = '';
let roomFitNoticeTimer = null;
let lookupTimer = null;
let lookupId = 0;
let resolvedVariantKey = '';
const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.05;
mount.appendChild(renderer.domElement);
renderer.domElement.style.touchAction = 'none';

const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.dampingFactor = 0.065;
controls.enablePan = false;
controls.minPolarAngle = THREE.MathUtils.degToRad(55);
controls.maxPolarAngle = THREE.MathUtils.degToRad(89);
controls.minDistance = 4;
controls.maxDistance = 16;

const STEPS = ['room', 'frame', 'turf', 'mat', 'monitor', 'projector', 'review'];
const ACCESSORIES = {
  turf: { name: 'Turf' }, mat: { name: 'Hitting mat' }, monitor: { name: 'Launch monitor' }, projector: { name: 'Projector' }
};
const state = { step: 'room', lastConfigurationStep: 'room', selectedGBlock: false, layer: null, width: 488, height: 305, depth: 610, frameWidth: 320, frameHeight: 280, projectorDistance: null, projectorLensHeight: null, projectorMount: 'center', accessories: { turf: {}, mat: {}, monitor: {}, projector: {} } };
const PLANNER_CACHE_KEY = 'gebblo-space-planner-selection-v1';
const SCALE = 100;
const EPP_DEPTH = 100;
const EPP_THICKNESS = 10;
const CORNER_SIZE = 40;
const REAR_CLEARANCE = 30;
const SCREEN_THICKNESS = 1;
const EPP_SEAM = 0.35;
const EPP_EDGE_RADIUS = 0.35;
// Turf roll coverage: two rolls cover up to 4 m; three rolls cover up to 6 m.
// The room-width control is capped below the three-roll maximum.
const TURF_TWO_ROLL_WIDTH = 400;
const TURF_THREE_ROLL_WIDTH = 600;
const TURF_DEPTH = 500;
const TURF_THICKNESS = 1;
const HITTING_MAT_WIDTH = 150;
const HITTING_MAT_DEPTH = 120;
const HITTING_MAT_SMALL_SIZE = 30;
const TEE_HOLE_RADIUS = 0.6;
const TEE_HOLE_RIM = 0.1;
// Acer GM560 / 1080p planning values. These are intentionally conservative
// first-pass recommendations, rather than a substitute for a site survey.
const PROJECTED_SCREEN_INSET = 20;
const PROJECTOR_MIN_DISTANCE_RATIO = 1.09;
const PROJECTOR_MAX_DISTANCE_RATIO = 1.3;
const SIDE_MOUNT_DISTANCE_OFFSET = 50;
const PROJECTOR_FRONT_CLEARANCE = 10;
const PROJECTOR_BODY_HEIGHT = 11;
const MIN_CEILING_MOUNT_FRAME_HEIGHT = 260;
// Hitting-station references. X runs left/right, Z runs from the screen toward
// the open front of the room. The large mat is centred 10 cm left of the
// enclosure centreline; the square pad remains attached to its right edge.
const LARGE_MAT_CENTRE_OFFSET = -10;
const SMALL_MAT_TOP_OFFSET = 11;
const SINGLE_LAYER_MAT_DISTANCE = 220;
const DOUBLE_LAYER_MAT_DISTANCE = 300;
const GOLFER_STANCE_BEHIND_TEE = 58;
// Positive Z is toward the open/front side of the room. Keep the 1080p ceiling
// projector behind the golfer (toward the open side) rather than above the ball.
const PROJECTOR_BEHIND_GOLFER_CLEARANCE = 35;
// The imported posed golfer includes a raised club. Its complete bounding box is
// taller than the person, so raw box-height scaling makes the stated body height
// look too short. This correction aligns the height control to the human body.
const GOLFER_MODEL_BODY_HEIGHT_RATIO = 0.84;
const GOLFER_REFERENCE_HEIGHT = 175;
const FLOOR_THICKNESS = 8;
// The floor mesh is centred below y = 0, so y = 0 remains the finished
// floor level used by turf, mats, frames and equipment.
const FLOOR_TOP = 0;
const room = new THREE.Group();
const assembly = new THREE.Group();
const turfGroup = new THREE.Group();
const matGroup = new THREE.Group();
const monitorGroup = new THREE.Group();
const projectorGroup = new THREE.Group();
const golferGroup = new THREE.Group();
let externalGolfer = null;
let golferLoadStarted = false;
let golferLoadFailed = false;
let golferLoadMessage = '';
const cameraOffset = new THREE.Vector3();
scene.add(room, turfGroup, matGroup, monitorGroup, assembly, projectorGroup, golferGroup);
scene.background = new THREE.Color(0xe8ebe6);

// Soft, even room fill keeps the screen neutral while revealing the product
// shapes. These lights change illumination only; the EPP, screen and wall
// materials retain their existing colours.
scene.add(new THREE.HemisphereLight(0xffffff, 0xb8c0b6, 1.75));
scene.add(new THREE.AmbientLight(0xf5f7f3, 0.42));
const softKeyLight = new THREE.DirectionalLight(0xffffff, 1.15);
softKeyLight.position.set(3.5, 7, 4);
scene.add(softKeyLight);
const roomRimLight = new THREE.DirectionalLight(0xdbe8ef, 0.52);
roomRimLight.position.set(-5, 5.5, -3.5);
scene.add(roomRimLight);

function createGrainTexture() {
  const textureCanvas = document.createElement('canvas');
  textureCanvas.width = 128;
  textureCanvas.height = 128;
  const context = textureCanvas.getContext('2d');
  const image = context.createImageData(128, 128);
  for (let pixel = 0; pixel < image.data.length; pixel += 4) {
    const value = 108 + Math.floor(Math.random() * 44);
    image.data[pixel] = value;
    image.data[pixel + 1] = value;
    image.data[pixel + 2] = value;
    image.data[pixel + 3] = 255;
  }
  context.putImageData(image, 0, 0);
  const texture = new THREE.CanvasTexture(textureCanvas);
  texture.colorSpace = THREE.NoColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(13, 13);
  return texture;
}

function createTurfTexture() {
  const textureCanvas = document.createElement('canvas');
  textureCanvas.width = 256;
  textureCanvas.height = 256;
  const context = textureCanvas.getContext('2d');
  context.fillStyle = '#486525';
  context.fillRect(0, 0, 256, 256);
  const blades = ['#294217', '#385420', '#5c7835', '#6f8d43'];
  for (let index = 0; index < 1500; index += 1) {
    const x = Math.random() * 256;
    const y = Math.random() * 256;
    const length = 3 + Math.random() * 10;
    context.strokeStyle = blades[Math.floor(Math.random() * blades.length)];
    context.globalAlpha = 0.35 + Math.random() * 0.45;
    context.lineWidth = Math.random() > 0.78 ? 1.2 : 0.65;
    context.beginPath();
    context.moveTo(x, y);
    context.lineTo(x + (Math.random() - 0.5) * 2.5, y - length);
    context.stroke();
  }
  context.globalAlpha = 1;
  const texture = new THREE.CanvasTexture(textureCanvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(14, 11);
  return texture;
}

function createHittingMatTexture() {
  const textureCanvas = document.createElement('canvas');
  textureCanvas.width = 384;
  textureCanvas.height = 384;
  const context = textureCanvas.getContext('2d');
  context.fillStyle = '#243f0f';
  context.fillRect(0, 0, 384, 384);
  // Dense short fibers create a compact hitting-mat surface, distinct from the wider turf texture.
  const fibers = ['#132a08', '#1c350c', '#35571b', '#4c6c2a', '#627d39', '#0e2105'];
  for (let index = 0; index < 19000; index += 1) {
    const x = Math.random() * 384;
    const y = Math.random() * 384;
    context.strokeStyle = fibers[Math.floor(Math.random() * fibers.length)];
    context.globalAlpha = 0.2 + Math.random() * 0.42;
    context.lineWidth = Math.random() > 0.9 ? 0.7 : 0.38;
    context.beginPath();
    context.moveTo(x, y);
    context.lineTo(x + (Math.random() - 0.5) * 0.75, y - 0.8 - Math.random() * 2.4);
    context.stroke();
  }
  context.globalAlpha = 1;
  const texture = new THREE.CanvasTexture(textureCanvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(11, 9);
  return texture;
}

const eppGrain = createGrainTexture();
const turfTexture = createTurfTexture();
const hittingMatTexture = createHittingMatTexture();
const floorMaterial = new THREE.MeshStandardMaterial({ color: 0x5c695e, roughness: 0.98 });
const turfMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff, map: turfTexture, bumpMap: turfTexture, bumpScale: 0.025, roughness: 0.9 });
const turfEdgeMaterial = new THREE.MeshStandardMaterial({ color: 0x385420, roughness: 0.98 });
const turfFiberMaterial = new THREE.LineBasicMaterial({ color: 0x6f8d43, transparent: true, opacity: 0.34 });
const hittingMatMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff, map: hittingMatTexture, bumpMap: hittingMatTexture, bumpScale: 0.026, roughness: 0.88, metalness: 0 });
const teeHoleMaterial = new THREE.MeshStandardMaterial({ color: 0x050806, roughness: 1 });
const teeHoleRimMaterial = new THREE.MeshStandardMaterial({ color: 0x19310a, roughness: 0.92 });
const projectorBodyMaterial = new THREE.MeshStandardMaterial({ color: 0xf6f7f5, roughness: 0.52, metalness: 0.04 });
const projectorTrimMaterial = new THREE.MeshStandardMaterial({ color: 0xd7d9d6, roughness: 0.46, metalness: 0.18 });
const projectorLensMaterial = new THREE.MeshStandardMaterial({ color: 0x10191b, roughness: 0.16, metalness: 0.55 });
const projectorGlassMaterial = new THREE.MeshStandardMaterial({ color: 0x335d6a, roughness: 0.08, metalness: 0.35, emissive: 0x102d39, emissiveIntensity: 0.28 });
const projectorVentMaterial = new THREE.MeshStandardMaterial({ color: 0xaeb4b0, roughness: 0.7, metalness: 0.12 });
// Keep the 4K projector visibly separate from the matte EPP enclosure: it is a
// graphite product shell with metallic trims, not another soft black module.
const projector4kBodyMaterial = new THREE.MeshStandardMaterial({ color: 0x293039, roughness: 0.26, metalness: 0.48 });
const projector4kTrimMaterial = new THREE.MeshStandardMaterial({ color: 0x6a727a, roughness: 0.23, metalness: 0.62 });
const projector4kVentMaterial = new THREE.MeshStandardMaterial({ color: 0x151a1e, roughness: 0.58, metalness: 0.25 });
const projector4kButtonMaterial = new THREE.MeshStandardMaterial({ color: 0x9aa2a5, roughness: 0.25, metalness: 0.55 });
const projectorBeamMaterial = new THREE.MeshBasicMaterial({ color: 0xe7f5df, transparent: true, opacity: 0.1, depthWrite: false, side: THREE.DoubleSide });
const monitorBodyMaterial = new THREE.MeshStandardMaterial({ color: 0x111313, roughness: 0.5, metalness: 0.18 });
const monitorTrimMaterial = new THREE.MeshStandardMaterial({ color: 0x262a29, roughness: 0.34, metalness: 0.28 });
const monitorVentMaterial = new THREE.MeshStandardMaterial({ color: 0x080909, roughness: 0.85, metalness: 0.04 });
const monitorSensorMaterial = new THREE.MeshStandardMaterial({ color: 0xf21721, emissive: 0xbc0010, emissiveIntensity: 1.45, roughness: 0.28, metalness: 0.12 });
const squareMonitorShellMaterial = new THREE.MeshStandardMaterial({ color: 0xd7d4cb, roughness: 0.42, metalness: 0.28 });
const squareMonitorFaceMaterial = new THREE.MeshStandardMaterial({ color: 0x111313, roughness: 0.42, metalness: 0.2 });
const squareMonitorScreenMaterial = new THREE.MeshStandardMaterial({ color: 0x0b1c24, emissive: 0x0b80b6, emissiveIntensity: 0.72, roughness: 0.22, metalness: 0.24 });
const squareMonitorCyanMaterial = new THREE.MeshStandardMaterial({ color: 0x6feaff, emissive: 0x31bad9, emissiveIntensity: 1.1, roughness: 0.24, metalness: 0.32 });
const wallMaterial = new THREE.MeshStandardMaterial({ color: 0xf0f2ef, roughness: 0.91, side: THREE.DoubleSide });
const ceilingMaterial = new THREE.MeshBasicMaterial({ color: 0xe4e7e3, side: THREE.DoubleSide });
const leftWallMaterial = wallMaterial.clone();
const rightWallMaterial = wallMaterial.clone();
const backWallMaterial = wallMaterial.clone();
leftWallMaterial.color.set(0xe6eae6);
rightWallMaterial.color.set(0xdde2dd);
backWallMaterial.color.set(0xf5f6f3);
const fadeCeilingMaterial = ceilingMaterial.clone();
[leftWallMaterial, rightWallMaterial, backWallMaterial, fadeCeilingMaterial].forEach((material) => {
  material.transparent = true;
  material.opacity = 1;
  material.depthWrite = false;
});
const epp40Material = new THREE.MeshStandardMaterial({ color: 0x171918, roughness: 0.84, bumpMap: eppGrain, bumpScale: 0.035 });
const epp50Material = new THREE.MeshStandardMaterial({ color: 0x1b1d1c, roughness: 0.82, bumpMap: eppGrain, bumpScale: 0.035 });
const triangleMaterial = new THREE.MeshStandardMaterial({ color: 0x1f2220, roughness: 0.85, bumpMap: eppGrain, bumpScale: 0.035 });
const grooveMaterial = new THREE.MeshStandardMaterial({ color: 0x3c423e, roughness: 0.95 });
const triangleSeamMaterial = new THREE.LineBasicMaterial({ color: 0x474d48, transparent: true, opacity: 0.82 });
const layerSeamMaterial = new THREE.LineBasicMaterial({ color: 0x474d48, transparent: true, opacity: 0.78 });
const screenMaterial = new THREE.MeshStandardMaterial({ color: 0xf8f8f4, roughness: 0.86, side: THREE.DoubleSide });
const gCurvePipeMaterial = new THREE.MeshStandardMaterial({ color: 0x222725, roughness: 0.36, metalness: 0.72 });
const gCurveFabricMaterial = new THREE.MeshStandardMaterial({ color: 0x101311, roughness: 0.94, side: THREE.DoubleSide });
const gBlockLogoMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.96, side: THREE.FrontSide, depthTest: true, depthWrite: false, toneMapped: false });
const gCurveLogoMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.98, side: THREE.DoubleSide, depthTest: true, depthWrite: false, toneMapped: false });
let gBlockLogoTexture = null;
let gCurveLogoTexture = null;

if (gBlockLogoUrl) {
  new THREE.TextureLoader().load(gBlockLogoUrl, (texture) => {
    texture.colorSpace = THREE.SRGBColorSpace;
    gBlockLogoTexture = texture;
    gBlockLogoMaterial.map = texture;
    gBlockLogoMaterial.needsUpdate = true;
    updateRoom();
  });
}
if (gCurveLogoUrl) {
  new THREE.TextureLoader().load(gCurveLogoUrl, (texture) => {
    texture.colorSpace = THREE.SRGBColorSpace;
    gCurveLogoTexture = texture;
    gCurveLogoMaterial.map = texture;
    gCurveLogoMaterial.needsUpdate = true;
    updateRoom();
  });
}

const floor = new THREE.Mesh(new THREE.BoxGeometry(1, cm(FLOOR_THICKNESS), 1), floorMaterial);
floor.receiveShadow = true;
// Room surfaces meet exactly on their shared edges.  Planes avoid the
// overlapping solid-wall faces that caused the outside-corner flicker.
const backWall = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), backWallMaterial);
const leftWall = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), leftWallMaterial);
const rightWall = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), rightWallMaterial);
const ceiling = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), fadeCeilingMaterial);
leftWall.rotation.y = Math.PI / 2;
rightWall.rotation.y = -Math.PI / 2;
ceiling.rotation.x = Math.PI / 2;
room.add(floor, backWall, leftWall, rightWall, ceiling);

// Room lighting is deliberately disabled for the current material inspection.

function cm(value) { return value / SCALE; }
function cmText(value) { return `${value} cm (${(value / 2.54).toFixed(1)} in)`; }
function cartDimensionText(value) { return `${value}cm (${(value / 2.54).toFixed(1)}in)`; }
function heightFeetInches(value) {
  const totalInches = Math.round(Number(value) / 2.54);
  return `${Math.floor(totalInches / 12)}'${totalInches % 12}\"`;
}
function hyphenate(key) { return key.replace(/([A-Z])/g, '-$1').toLowerCase(); }
function rangeValue(key) { return document.getElementById(`${hyphenate(key)}-range`); }
function savePlannerState() {
  try {
    localStorage.setItem(PLANNER_CACHE_KEY, JSON.stringify({
      state: { ...state, step: 'room', lastConfigurationStep: 'room' },
      selectedFrameProduct
    }));
  } catch (_) {}
}
function restorePlannerState() {
  try {
    const cached = JSON.parse(localStorage.getItem(PLANNER_CACHE_KEY) || 'null');
    if (!cached?.state) return;
    ['width', 'height', 'depth', 'frameWidth', 'frameHeight'].forEach((key) => {
      const input = rangeValue(key);
      const value = Number(cached.state[key]);
      if (input && Number.isFinite(value)) state[key] = Math.min(Number(input.max), Math.max(Number(input.min), value));
    });
    state.selectedGBlock = Boolean(cached.state.selectedGBlock && cached.selectedFrameProduct?.handle);
    state.layer = cached.state.layer === 'Double Layer' ? 'Double Layer' : (state.selectedGBlock ? 'Single Layer' : null);
    state.projectorDistance = Number.isFinite(Number(cached.state.projectorDistance)) ? Number(cached.state.projectorDistance) : null;
    state.projectorLensHeight = Number.isFinite(Number(cached.state.projectorLensHeight)) ? Number(cached.state.projectorLensHeight) : null;
    state.projectorMount = cached.state.projectorMount === 'side' ? 'side' : 'center';
    selectedFrameProduct = state.selectedGBlock ? cached.selectedFrameProduct : null;
    ['turf', 'mat', 'monitor', 'projector'].forEach((step) => {
      const saved = cached.state.accessories?.[step];
      state.accessories[step] = saved?.selected ? saved : {};
    });
  } catch (_) {}
}
function selectedFrameType() { return selectedFrameProduct?.type || 'gblock'; }
function isGCurve() { return selectedFrameType() === 'gcurve'; }
function frameDepth() { return isGCurve() ? GCURVE.depth : (state.layer === 'Double Layer' ? 200 : 100); }
function frameDepthText() { return cmText(frameDepth()); }
function frameDepthImperial() { return state.layer === 'Double Layer' ? `6'7"` : `3'4"`; }
function frameStatusText() {
  if (isGCurve()) return `G-Curve | ${cmText(GCURVE.depth)} D × ${cmText(GCURVE.width)} W × ${cmText(GCURVE.height)} H`;
  return `${selectedFrameProduct?.title || 'G-Block'} | ${state.layer} | ${frameDepthText()} D × ${cmText(state.frameWidth)} W × ${cmText(state.frameHeight)} H`;
}
function maxFrameValue(key) { return key === 'frameWidth' ? Math.min(520, state.width - 20) : Math.min(330, state.height - 10); }
function snapDown(value) { return Math.floor(value / 10) * 10; }

function packSegments(length) {
  for (let count50 = Math.floor(length / 50); count50 >= 0; count50 -= 1) {
    const remainder = length - count50 * 50;
    if (remainder >= 0 && remainder % 40 === 0) return [...Array(count50).fill(50), ...Array(remainder / 40).fill(40)];
  }
  return null;
}

function buildPlan() {
  const vertical = packSegments(state.frameHeight - CORNER_SIZE);
  const top = packSegments(state.frameWidth - CORNER_SIZE * 2);
  const bottom = packSegments(state.frameWidth - EPP_THICKNESS * 2);
  if (!vertical || !top || !bottom) return null;
  const layers = state.layer === 'Double Layer' ? 2 : 1;
  const count = (segments, value) => segments.filter((segment) => segment === value).length;
  return {
    vertical, top, bottom, layers,
    counts: {
      l: 2 * layers,
      rect40: (count(vertical, 40) * 2 + count(top, 40)) * layers,
      rect50: (count(vertical, 50) * 2 + count(top, 50)) * layers,
      tri40: count(bottom, 40),
      tri50: count(bottom, 50)
    }
  };
}

function normalizeFrameSelection() {
  state.frameWidth = Math.max(200, Math.min(state.frameWidth, snapDown(maxFrameValue('frameWidth'))));
  state.frameHeight = Math.max(200, Math.min(state.frameHeight, snapDown(maxFrameValue('frameHeight'))));
}

function syncFrameRangeLimits() {
  const widthInput = rangeValue('frameWidth');
  const heightInput = rangeValue('frameHeight');
  const widthMax = Math.max(200, snapDown(maxFrameValue('frameWidth')));
  const heightMax = Math.max(200, snapDown(maxFrameValue('frameHeight')));
  widthInput.max = widthMax;
  heightInput.max = heightMax;
  normalizeFrameSelection();
}

function frameFits() {
  if (isGCurve()) return state.width >= GCURVE.minimumRoom.width && state.height >= GCURVE.minimumRoom.height && state.depth >= GCURVE.minimumRoom.depth;
  return state.frameWidth + 20 <= state.width &&
    state.frameHeight + 10 <= state.height &&
    state.depth >= frameDepth() + SCREEN_THICKNESS + REAR_CLEARANCE &&
    Boolean(buildPlan());
}

function gCurveRequirement() {
  const missing = [];
  if (state.width < GCURVE.minimumRoom.width) missing.push(`Requires room width of at least ${GCURVE.minimumRoom.width} cm.`);
  if (state.height < GCURVE.minimumRoom.height) missing.push(`Requires room height of at least ${GCURVE.minimumRoom.height} cm.`);
  if (state.depth < GCURVE.minimumRoom.depth) missing.push(`Requires room depth of at least ${GCURVE.minimumRoom.depth} cm.`);
  return missing;
}

function removeIncompatibleGCurve() {
  if (!isGCurve() || frameFits()) return false;
  const requirements = gCurveRequirement().join(' ');
  roomFitNotice = `G-Curve was removed because it no longer fits this room. ${requirements}`;
  state.selectedGBlock = false;
  state.layer = null;
  selectedFrameProduct = null;
  selectedVariant = null;
  resolvedVariantKey = '';
  return true;
}

function clearAssembly() {
  while (assembly.children.length) {
    const child = assembly.children[0];
    assembly.remove(child);
    child.geometry?.dispose();
  }
}

function addBox(parent, width, height, depth, x, y, z, material, isEpp = false) {
  const seam = isEpp ? EPP_SEAM : 0;
  const geometry = isEpp
    ? new RoundedBoxGeometry(
      cm(Math.max(width - seam, 0.1)),
      cm(Math.max(height - seam, 0.1)),
      cm(Math.max(depth - seam, 0.1)),
      3,
      cm(EPP_EDGE_RADIUS)
    )
    : new THREE.BoxGeometry(cm(width), cm(height), cm(depth));
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(cm(x), cm(y), cm(z));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  parent.add(mesh);
}

function clearTurf() {
  while (turfGroup.children.length) {
    const child = turfGroup.children[0];
    turfGroup.remove(child);
    child.traverse?.((mesh) => { mesh.geometry?.dispose?.(); });
  }
}

function turfIsSelected() {
  return Boolean(state.accessories.turf?.selected);
}

function turfRollCount(variant) {
  const value = `${variant?.title || ''} ${variant?.sku || ''}`.toLowerCase();
  if (/(^|\D)3(\D|$)|three|三/.test(value)) return 3;
  if (/(^|\D)2(\D|$)|two|两/.test(value)) return 2;
  return 0;
}

function turfCoverageWidth() {
  return turfRollCount(state.accessories.turf?.variant) >= 3 ? TURF_THREE_ROLL_WIDTH : TURF_TWO_ROLL_WIDTH;
}

function shouldDefaultToThreeTurfRolls() {
  return isGCurve() || state.width > TURF_TWO_ROLL_WIDTH;
}

function preferredTurfVariant(variants) {
  const available = variants.filter((item) => item.availableForSale);
  const preferredRolls = shouldDefaultToThreeTurfRolls() ? 3 : 2;
  return available.find((item) => turfRollCount(item) === preferredRolls) || available.find((item) => turfRollCount(item) === 2) || available.find((item) => turfRollCount(item) === 3) || available[0] || null;
}

function applyDefaultTurfVariant() {
  const selection = state.accessories.turf;
  if (!selection?.selected || selection.turfRollManuallySelected || !selection.variants?.length) return;
  const variant = preferredTurfVariant(selection.variants);
  if (variant) selection.variant = variant;
}

function renderTurfRollOptions() {
  const options = document.getElementById('turf-roll-options');
  const choice = document.getElementById('turf-roll-choice');
  const note = document.getElementById('turf-coverage-note');
  const selection = state.accessories.turf;
  const variants = (selection?.variants || []).filter((item) => turfRollCount(item) >= 2);
  const isTurfStep = state.step === 'turf' && Boolean(selection?.selected);
  options.hidden = !isTurfStep || variants.length === 0;
  if (options.hidden) return;
  choice.replaceChildren();
  variants.forEach((variant) => {
    const rolls = turfRollCount(variant);
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.turfVariantId = variant.id;
    button.textContent = variant.availableForSale ? variant.title : `${variant.title} — Sold out`;
    button.classList.toggle('is-active', selection.variant?.id === variant.id);
    button.disabled = !variant.availableForSale;
    choice.append(button);
  });
  note.hidden = false;
  note.textContent = `2 Rolls: up to ${cmText(TURF_TWO_ROLL_WIDTH)}. 3 Rolls: up to ${cmText(TURF_THREE_ROLL_WIDTH)}.`;
}

function matIsSelected() {
  return Boolean(state.accessories.mat?.selected);
}

function monitorIsSelected() {
  return Boolean(state.accessories.monitor?.selected);
}

function selectedMonitorIsOmni() {
  const monitor = state.accessories.monitor || {};
  return monitorIsSelected() && /\bomni\b/i.test(`${monitor.title || ''} ${monitor.handle || ''}`);
}

function projectorIsSelected() {
  return Boolean(state.accessories.projector?.selected);
}

function selectedProjectorIs1080p() {
  const projector = state.accessories.projector || {};
  const label = `${projector.title || ''} ${projector.handle || ''}`;
  return projectorIsSelected() && !/4k/i.test(label) && /1080p|acer|gm560/i.test(label);
}

function selectedProjectorIs4k() {
  const projector = state.accessories.projector || {};
  return projectorIsSelected() && /4k/i.test(`${projector.title || ''} ${projector.handle || ''}`);
}

function activeFrameDimensions() {
  return isGCurve()
    ? { width: GCURVE.width, height: GCURVE.height, depth: GCURVE.depth }
    : { width: state.frameWidth, height: state.frameHeight, depth: frameDepth() };
}
function ceilingMountAllowed() { return true; }
function enforceProjectorMountEligibility() {}

function projectorPlan() {
  if (!state.selectedGBlock || !projectorIsSelected()) return null;
  const frame = activeFrameDimensions();
  const screenWidth = Math.max(0, frame.width - PROJECTED_SCREEN_INSET);
  const screenHeight = Math.max(0, frame.height - PROJECTED_SCREEN_INSET);
  const sideMounted = state.projectorMount === 'side';
  const sideOffset = sideMounted ? SIDE_MOUNT_DISTANCE_OFFSET : 0;
  const opticalMinDistance = frame.width * PROJECTOR_MIN_DISTANCE_RATIO + sideOffset;
  const opticalMaxDistance = frame.width * PROJECTOR_MAX_DISTANCE_RATIO + sideOffset;
  const screenFrontZ = -state.depth / 2 + REAR_CLEARANCE + SCREEN_THICKNESS;
  const minDistance = opticalMinDistance;
  const maxDistance = opticalMaxDistance;
  const minLensHeight = Math.max(0, frame.height - 20);
  const maxLensHeight = Math.min(frame.height - 10, state.height - PROJECTOR_BODY_HEIGHT / 2);
  const availableDistance = state.depth / 2 - screenFrontZ - PROJECTOR_FRONT_CLEARANCE;
  const safeMaxDistance = Math.max(0, Math.min(maxDistance, availableDistance));
  const depthFits = safeMaxDistance >= minDistance;
  const heightFits = maxLensHeight >= minLensHeight;
  const mountDistance = depthFits
    ? Math.max(minDistance, Math.min(safeMaxDistance, state.projectorDistance ?? (minDistance + safeMaxDistance) / 2))
    : safeMaxDistance;
  const mountLensHeight = heightFits
    ? Math.max(minLensHeight, Math.min(maxLensHeight, state.projectorLensHeight ?? maxLensHeight))
    : minLensHeight;
  return { frame, screenWidth, screenHeight, opticalMinDistance, opticalMaxDistance, minDistance, maxDistance, minLensHeight, maxLensHeight, lensHeight: mountLensHeight, availableDistance, depthFits, heightFits, screenFrontZ, mountDistance, safeMaxDistance, roomLimited: safeMaxDistance < maxDistance, sideMounted };
}

function projectorVisualPlan() {
  const calibratedPlan = projectorPlan();
  if (calibratedPlan) return calibratedPlan;
  if (!state.selectedGBlock || !selectedProjectorIs4k()) return null;

  // The 4K projector's optical data is not confirmed yet. This only provides a
  // sensible visual location; it deliberately creates no placement recommendation.
  const frame = activeFrameDimensions();
  const screenWidth = Math.max(0, frame.width - PROJECTED_SCREEN_INSET);
  const screenHeight = Math.max(0, frame.height - PROJECTED_SCREEN_INSET);
  const screenFrontZ = -state.depth / 2 + REAR_CLEARANCE + SCREEN_THICKNESS;
  const availableDistance = Math.max(0, state.depth / 2 - screenFrontZ - PROJECTOR_FRONT_CLEARANCE);
  const requestedDistance = frame.width * (PROJECTOR_MIN_DISTANCE_RATIO + PROJECTOR_MAX_DISTANCE_RATIO) / 2;
  const mountDistance = Math.min(availableDistance, requestedDistance);
  const lensHeight = Math.max(20, Math.min(frame.height - 10, state.height - 8));
  return { frame, screenWidth, screenHeight, screenFrontZ, mountDistance, lensHeight, isVisualOnly: true };
}

function projectorWarnings(plan) {
  if (!plan) return [];
  const warnings = [];
  if (!plan.depthFits) warnings.push(`Room depth allows up to ${Math.round(plan.safeMaxDistance)} cm from the screen, while this setup requires at least ${Math.ceil(plan.minDistance)} cm.`);
  else if (plan.roomLimited) warnings.push(`Normal maximum distance is ${Math.ceil(plan.maxDistance)} cm, but this room limits installation to ${Math.floor(plan.safeMaxDistance)} cm.`);
  if (!plan.heightFits) warnings.push(`Room height cannot accommodate the ${Math.ceil(plan.minLensHeight)}–${Math.floor(plan.maxLensHeight)} cm projector-height range.`);
  return warnings;
}

function matLayout() {
  const backEdge = -state.depth / 2;
  const turfDepth = Math.min(state.depth, TURF_DEPTH);
  const turfFrontEdge = backEdge + turfDepth;
  const matDistance = state.layer === 'Double Layer' ? DOUBLE_LAYER_MAT_DISTANCE : SINGLE_LAYER_MAT_DISTANCE;
  // The visible screen plane is the dependable frame reference for both layer
  // options. The large mat's screen-side (top) edge starts at this distance.
  const screenPlaneZ = backEdge + REAR_CLEARANCE + SCREEN_THICKNESS;
  const requestedCentreZ = screenPlaneZ + matDistance + HITTING_MAT_DEPTH / 2 + (isGCurve() ? 40 : 0);
  const minimumCentreZ = backEdge + HITTING_MAT_DEPTH / 2 + 5;
  const maximumCentreZ = turfFrontEdge - HITTING_MAT_DEPTH / 2 - 5;
  const centerZ = THREE.MathUtils.clamp(requestedCentreZ, minimumCentreZ, maximumCentreZ);
  // G-Block keeps the large mat 10 cm left of centre. For G-Curve, its right
  // tee hole must sit 10 cm right of the centreline: with the hole 60 cm from
  // the mat centre, that makes the large mat centre -50 cm.
  const minimumCentreX = -state.width / 2 + HITTING_MAT_WIDTH / 2 + 5;
  const maximumCentreX = state.width / 2 - HITTING_MAT_WIDTH / 2 - HITTING_MAT_SMALL_SIZE - 5;
  const requestedCentreX = isGCurve() ? -50 : LARGE_MAT_CENTRE_OFFSET;
  const centerX = THREE.MathUtils.clamp(requestedCentreX, Math.min(minimumCentreX, maximumCentreX), Math.max(minimumCentreX, maximumCentreX));
  return {
    centerX,
    centerZ,
    big: { minX: centerX - HITTING_MAT_WIDTH / 2, maxX: centerX + HITTING_MAT_WIDTH / 2, minZ: centerZ - HITTING_MAT_DEPTH / 2, maxZ: centerZ + HITTING_MAT_DEPTH / 2 },
    // The square pad touches the large mat's right side, with its top edge
    // positioned 11 cm below the large mat's screen-side/top edge.
    small: { minX: centerX + HITTING_MAT_WIDTH / 2, maxX: centerX + HITTING_MAT_WIDTH / 2 + HITTING_MAT_SMALL_SIZE, minZ: centerZ - HITTING_MAT_DEPTH / 2 + SMALL_MAT_TOP_OFFSET, maxZ: centerZ - HITTING_MAT_DEPTH / 2 + SMALL_MAT_TOP_OFFSET + HITTING_MAT_SMALL_SIZE }
  };
}

// The right tee position on the large hitting mat is the primary hitting point:
// 135 cm from the left edge and 80 cm down from the screen-side edge. The golfer,
// launch monitor and 1080p projector all use this shared physical reference.
function primaryTeePosition() {
  const { big } = matLayout();
  return { x: big.minX + 135, z: big.maxZ - 80 };
}

function addTurfPanel(width, depth, x, z, turfCenterY) {
  if (width <= 0.1 || depth <= 0.1) return;
  addBox(turfGroup, width, TURF_THICKNESS, depth, x, turfCenterY, z, turfMaterial);
}

function buildTurf() {
  clearTurf();
  if (!turfIsSelected()) return;
  const width = Math.min(state.width, turfCoverageWidth());
  const depth = Math.min(state.depth, TURF_DEPTH);
  const backEdge = -state.depth / 2;
  const centerZ = backEdge + depth / 2;
  const turfCenterY = FLOOR_TOP + TURF_THICKNESS / 2;
  const turfSurfaceY = FLOOR_TOP + TURF_THICKNESS;
  if (!matIsSelected()) {
    addTurfPanel(width, depth, 0, centerZ, turfCenterY);
  } else {
    const { big, small } = matLayout();
    const turfMinX = -width / 2;
    const turfMaxX = width / 2;
    const turfMinZ = backEdge;
    const turfMaxZ = backEdge + depth;
    addTurfPanel(Math.max(0, big.minX - turfMinX), depth, (turfMinX + big.minX) / 2, centerZ, turfCenterY);
    addTurfPanel(Math.max(0, turfMaxX - small.maxX), depth, (small.maxX + turfMaxX) / 2, centerZ, turfCenterY);
    addTurfPanel(Math.max(0, small.maxX - big.minX), Math.max(0, big.minZ - turfMinZ), (big.minX + small.maxX) / 2, (turfMinZ + big.minZ) / 2, turfCenterY);
    addTurfPanel(Math.max(0, small.maxX - big.minX), Math.max(0, turfMaxZ - big.maxZ), (big.minX + small.maxX) / 2, (big.maxZ + turfMaxZ) / 2, turfCenterY);
    // The small mat begins 11 cm below the large mat's top edge. Restore turf
    // in the exposed strip above it rather than leaving its former position bare.
    addTurfPanel(HITTING_MAT_SMALL_SIZE, Math.max(0, small.minZ - big.minZ), (small.minX + small.maxX) / 2, (big.minZ + small.minZ) / 2, turfCenterY);
    addTurfPanel(HITTING_MAT_SMALL_SIZE, Math.max(0, big.maxZ - small.maxZ), (small.minX + small.maxX) / 2, (small.maxZ + big.maxZ) / 2, turfCenterY);
  }
  // The turf's dense procedural texture carries the grass detail. Avoid an overlay grid here,
  // because it would incorrectly continue across the hitting-mat cut-out.
}

function clearMat() {
  while (matGroup.children.length) {
    const child = matGroup.children[0];
    matGroup.remove(child);
    child.traverse?.((mesh) => { mesh.geometry?.dispose?.(); });
  }
}

function addTeeHole(x, z, surfaceY) {
  const hole = new THREE.Mesh(new THREE.CylinderGeometry(cm(TEE_HOLE_RADIUS), cm(TEE_HOLE_RADIUS), cm(0.22), 32), teeHoleMaterial);
  hole.position.set(cm(x), cm(surfaceY + 0.02), cm(z));
  hole.receiveShadow = true;
  matGroup.add(hole);
  const rim = new THREE.Mesh(new THREE.TorusGeometry(cm(TEE_HOLE_RADIUS), cm(TEE_HOLE_RIM), 6, 24), teeHoleRimMaterial);
  rim.rotation.x = Math.PI / 2;
  rim.position.set(cm(x), cm(surfaceY + 0.15), cm(z));
  matGroup.add(rim);
}

function buildHittingMat() {
  clearMat();
  if (!matIsSelected()) return;
  const { centerX, centerZ, big, small } = matLayout();
  const matCenterY = FLOOR_TOP + TURF_THICKNESS / 2;
  const matSurfaceY = FLOOR_TOP + TURF_THICKNESS;
  addBox(matGroup, HITTING_MAT_WIDTH, TURF_THICKNESS, HITTING_MAT_DEPTH, centerX, matCenterY, centerZ, hittingMatMaterial);
  addBox(matGroup, HITTING_MAT_SMALL_SIZE, TURF_THICKNESS, HITTING_MAT_SMALL_SIZE, (small.minX + small.maxX) / 2, matCenterY, (small.minZ + small.maxZ) / 2, hittingMatMaterial);
  // Coordinates are measured from the large mat's lower-left corner as seen in the planner.
  // X runs right along the 150 cm edge; Y runs upward toward the screen/back wall.
  // Three.js uses Z for that second direction, with the visual Y axis being negative Z.
  addTeeHole(big.minX + 15, big.maxZ - 40, matSurfaceY);
  addTeeHole(big.minX + 135, big.maxZ - 80, matSurfaceY);
}

function clearMonitor() {
  while (monitorGroup.children.length) {
    const child = monitorGroup.children[0];
    monitorGroup.remove(child);
    child.traverse?.((mesh) => { mesh.geometry?.dispose?.(); });
  }
}

function addMonitorBox(width, height, depth, x, y, z, material, radius = 0.8) {
  const mesh = new THREE.Mesh(new RoundedBoxGeometry(cm(width), cm(height), cm(depth), 4, cm(radius)), material);
  mesh.position.set(cm(x), cm(y), cm(z));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  monitorGroup.add(mesh);
  return mesh;
}

function addMonitorStrut(start, end, radius, material) {
  const startVector = new THREE.Vector3(cm(start.x), cm(start.y), cm(start.z));
  const endVector = new THREE.Vector3(cm(end.x), cm(end.y), cm(end.z));
  const direction = endVector.clone().sub(startVector);
  const strut = new THREE.Mesh(
    new THREE.CylinderGeometry(cm(radius), cm(radius * 1.12), direction.length(), 8),
    material
  );
  strut.position.copy(startVector.clone().add(endVector).multiplyScalar(0.5));
  strut.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
  strut.castShadow = true;
  strut.receiveShadow = true;
  monitorGroup.add(strut);
  return strut;
}

function addOmniSensorModule(x, y, z) {
  // The entire sensing face is deliberately placed on the left-facing (-X) side.
  addMonitorBox(0.8, 8.4, 9.4, x, y, z, squareMonitorFaceMaterial, 0.65);
  const camera = new THREE.Mesh(new THREE.SphereGeometry(cm(1.45), 16, 10), monitorVentMaterial);
  camera.position.set(cm(x - 0.58), cm(y), cm(z));
  camera.castShadow = true;
  monitorGroup.add(camera);
  [-2.8, 2.8].forEach((zOffset) => {
    [-2.25, 0, 2.25].forEach((yOffset) => {
      const led = new THREE.Mesh(new THREE.SphereGeometry(cm(0.56), 12, 8), monitorSensorMaterial);
      led.position.set(cm(x - 0.66), cm(y + yOffset), cm(z + zOffset));
      led.castShadow = true;
      monitorGroup.add(led);
    });
  });
}

function monitorPositionOnSquarePad(small, width, depth) {
  const inset = 2;
  // Both monitor bodies sit on the square pad's right-hand side and are
  // centred front-to-back. Their sensor surfaces remain on the left (-X).
  return {
    x: THREE.MathUtils.clamp(small.maxX - inset - width / 2, small.minX + inset + width / 2, small.maxX - inset - width / 2),
    z: THREE.MathUtils.clamp((small.minZ + small.maxZ) / 2, small.minZ + inset + depth / 2, small.maxZ - inset - depth / 2)
  };
}

function buildOmniLaunchMonitor(small, surfaceY) {
  // The Square Golf Omni is a separate product: tall white/black tower, red sensor
  // modules and a tripod. Its footprint uses the same fixed mat reference as the
  // compact black launch monitor, with the sensing face pointing to the left.
  const width = 11.5;
  const depth = 10.5;
  const bodyHeight = 50;
  const bodyBottom = surfaceY + 12;
  const { x, z } = monitorPositionOnSquarePad(small, width, depth);
  const bodyCenterY = bodyBottom + bodyHeight / 2;
  const body = addMonitorBox(width, bodyHeight, depth, x, bodyCenterY, z, squareMonitorShellMaterial, 1.35);
  body.name = 'Square Golf Omni body';

  // Black sensing column on the product's left side, plus the visible cyan top seam.
  addMonitorBox(0.78, bodyHeight - 3, depth - 1.1, x - width / 2 - 0.08, bodyCenterY, z, squareMonitorFaceMaterial, 0.5);
  addMonitorBox(width + 0.3, 0.62, depth + 0.22, x, bodyBottom + bodyHeight - 0.2, z, squareMonitorCyanMaterial, 0.28);

  // Four red-light camera modules and a central blue display make the model legible
  // at planner scale without relying on external textures.
  [bodyBottom + 42, bodyBottom + 32, bodyBottom + 22, bodyBottom + 6.5].forEach((y) => {
    addOmniSensorModule(x - width / 2 - 0.3, y, z);
  });
  addMonitorBox(0.72, 13.2, 5.4, x - width / 2 - 0.34, bodyBottom + 16, z, squareMonitorScreenMaterial, 0.38);
  [11.6, 15.5, 19.4].forEach((y, index) => {
    addMonitorBox(0.8, 0.48, index === 1 ? 3.8 : 2.8, x - width / 2 - 0.72, bodyBottom + y, z, squareMonitorCyanMaterial, 0.1);
  });

  // Slim vertical ribs keep the otherwise plain white side recognisable as a product housing.
  for (let index = 0; index < 7; index += 1) {
    addMonitorBox(0.3, 33, 0.26, x + width / 2 + 0.05, bodyBottom + 28, z - 3.55 + index * 1.18, monitorVentMaterial, 0.08);
  }

  const legRadius = 1.12;
  const hubY = surfaceY + 9.2;
  const footY = surfaceY + legRadius;
  addMonitorBox(8.6, 5.7, 8.6, x, hubY, z, squareMonitorFaceMaterial, 0.9);
  addMonitorStrut({ x, y: hubY - 1.05, z }, { x: x - 16, y: footY, z: z - 10.5 }, legRadius, monitorTrimMaterial);
  addMonitorStrut({ x, y: hubY - 1.05, z }, { x: x + 16, y: footY, z: z - 10.5 }, legRadius, monitorTrimMaterial);
  addMonitorStrut({ x, y: hubY - 1.05, z }, { x, y: footY, z: z + 14.5 }, legRadius, monitorTrimMaterial);
}

function buildLaunchMonitor() {
  clearMonitor();
  monitorGroup.scale.setScalar(1);
  monitorGroup.position.set(0, 0, 0);
  if (!monitorIsSelected()) return;

  const { small } = matLayout();
  const surfaceY = FLOOR_TOP + (matIsSelected() || turfIsSelected() ? TURF_THICKNESS : 0);
  if (selectedMonitorIsOmni()) {
    buildOmniLaunchMonitor(small, surfaceY);
    const omniCentre = new THREE.Box3().setFromObject(monitorGroup).getCenter(new THREE.Vector3());
    monitorGroup.scale.setScalar(0.8);
    monitorGroup.position.copy(omniCentre.multiplyScalar(0.2));
    return;
  }

  const monitorWidth = 9;
  const monitorHeight = 34;
  const monitorDepth = 8;
  // Place the compact model at the square pad's right-centre. Its red sensor
  // face is deliberately oriented toward -X (left), matching the Omni model.
  const { x: monitorX, z: monitorZ } = monitorPositionOnSquarePad(small, monitorWidth, monitorDepth);
  const monitorY = surfaceY + monitorHeight / 2;

  const body = addMonitorBox(monitorWidth, monitorHeight, monitorDepth, monitorX, monitorY, monitorZ, monitorBodyMaterial, 1.05);
  body.name = 'Launch monitor body';
  addMonitorBox(monitorWidth + 0.65, 1.15, monitorDepth + 0.55, monitorX, surfaceY + monitorHeight - 0.2, monitorZ, monitorTrimMaterial, 0.65);
  addMonitorBox(monitorWidth + 1.1, 1.0, monitorDepth + 1.2, monitorX, surfaceY + 0.5, monitorZ, monitorTrimMaterial, 0.45);

  // Subtle vertical vent ribs on the outer face keep the body readable in dark scenes.
  for (let index = 0; index < 7; index += 1) {
    addMonitorBox(0.36, 22, 0.36, monitorX + monitorWidth / 2 + 0.04, surfaceY + 17, monitorZ - 2.5 + index * 0.82, monitorVentMaterial, 0.08);
  }

  // A 3 × 3 red sensor array is placed on the left-facing side of the unit.
  [-5.2, 0, 5.2].forEach((verticalOffset, row) => {
    [-2.2, 0, 2.2].forEach((depthOffset) => {
      if (row === 1 && depthOffset === 0) return;
      const sensor = new THREE.Mesh(new THREE.SphereGeometry(cm(0.72), 12, 8), monitorSensorMaterial);
      sensor.position.set(cm(monitorX - monitorWidth / 2 - 0.25), cm(surfaceY + 16 + verticalOffset), cm(monitorZ + depthOffset));
      sensor.castShadow = true;
      monitorGroup.add(sensor);
    });
  });
}

function clearProjector() {
  while (projectorGroup.children.length) {
    const child = projectorGroup.children[0];
    projectorGroup.remove(child);
    child.traverse?.((mesh) => { mesh.geometry?.dispose?.(); });
  }
}

function addProjectorBox(width, height, depth, x, y, z, material, radius = 0.9) {
  const geometry = new RoundedBoxGeometry(cm(width), cm(height), cm(depth), 3, cm(radius));
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(cm(x), cm(y), cm(z));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  projectorGroup.add(mesh);
  return mesh;
}

function buildProjector() {
  clearProjector();
  const plan = projectorVisualPlan();
  if (!plan) return;

  const is4k = selectedProjectorIs4k();
  const bodyWidth = is4k ? 49 : 36;
  const bodyHeight = is4k ? 16 : PROJECTOR_BODY_HEIGHT;
  const bodyDepth = is4k ? 34 : 26;
  const bodyMaterial = is4k ? projector4kBodyMaterial : projectorBodyMaterial;
  const trimMaterial = is4k ? projector4kTrimMaterial : projectorTrimMaterial;
  const ventMaterial = is4k ? projector4kVentMaterial : projectorVentMaterial;
  const lensOffsetX = is4k ? -6 : 3.8;
  const lensY = Math.min(state.height - bodyHeight / 2 - 7, Math.max(bodyHeight / 2 + 8, plan.lensHeight));
  const lensZ = plan.screenFrontZ + plan.mountDistance;
  // Both projector models use the same centred-ceiling or right-side mounting
  // logic; their body dimensions remain product-specific below.
  const sideMounted = state.projectorMount === 'side';
  // Side projection is installed on the right: keep the lens just inside the
  // right frame upright on a fixed vertical plane, while it continues to face
  // the main screen and remains below the frame top.
  const sideLensX = Math.min(plan.frame.width / 2 - 18, state.width / 2 - 24);
  const bodyX = sideMounted ? sideLensX - lensOffsetX : 0;
  const body = addProjectorBox(bodyWidth, bodyHeight, bodyDepth, bodyX, lensY, lensZ, bodyMaterial, is4k ? 2.5 : 1.5);
  body.name = is4k ? 'Hisense-style 4K projector body' : 'Acer 1080p projector body';

  // Front lens and its mounting ring face the screen/back wall (-Z).
  const ringRadius = is4k ? 9.4 : 6.8;
  const lensRadius = is4k ? 6.9 : 4.6;
  const ring = new THREE.Mesh(new THREE.CylinderGeometry(cm(ringRadius), cm(ringRadius), cm(is4k ? 1.8 : 1.25), 32), trimMaterial);
  ring.rotation.x = Math.PI / 2;
  ring.position.set(cm(bodyX + lensOffsetX), cm(lensY), cm(lensZ - bodyDepth / 2 - 0.6));
  ring.castShadow = true;
  projectorGroup.add(ring);
  const lens = new THREE.Mesh(new THREE.CylinderGeometry(cm(lensRadius), cm(lensRadius), cm(is4k ? 2.2 : 1.55), 32), projectorLensMaterial);
  lens.rotation.x = Math.PI / 2;
  lens.position.set(cm(bodyX + lensOffsetX), cm(lensY), cm(lensZ - bodyDepth / 2 - 1.45));
  projectorGroup.add(lens);
  const glass = new THREE.Mesh(new THREE.CircleGeometry(cm(is4k ? 5.7 : 3.65), 28), projectorGlassMaterial);
  glass.rotation.y = Math.PI;
  glass.position.set(cm(bodyX + lensOffsetX), cm(lensY), cm(lensZ - bodyDepth / 2 - (is4k ? 2.7 : 2.02)));
  projectorGroup.add(glass);

  // White Acer uses side intake ribs; the black 4K unit gets a full perforated-style face.
  if (!is4k) {
    for (let index = 0; index < 7; index += 1) {
      addProjectorBox(10, 0.48, 0.75, bodyX - 11.2, lensY - 3.4 + index * 1.12, lensZ - bodyDepth / 2 - 0.38, ventMaterial, 0.15);
    }
  } else {
    for (let index = 0; index < 10; index += 1) {
      addProjectorBox(bodyWidth - 4, 0.42, 0.8, bodyX, lensY - 5.6 + index * 1.24, lensZ - bodyDepth / 2 - 0.5, ventMaterial, 0.1);
    }
    [-7, 0, 7].forEach((offset) => {
      const button = new THREE.Mesh(new THREE.CylinderGeometry(cm(1.25), cm(1.25), cm(0.55), 16), projector4kButtonMaterial);
      button.rotation.x = Math.PI / 2;
      button.position.set(cm(bodyX + offset), cm(lensY + bodyHeight / 2 + 0.28), cm(lensZ - 4));
      button.castShadow = true;
      projectorGroup.add(button);
    });
  }
  const indicator = new THREE.Mesh(new THREE.SphereGeometry(cm(0.7), 12, 8), new THREE.MeshStandardMaterial({ color: 0x5f8e43, emissive: 0x4d842c, emissiveIntensity: 0.65, roughness: 0.35 }));
  indicator.position.set(cm(bodyX + (is4k ? 17 : 14)), cm(lensY), cm(lensZ - bodyDepth / 2 - 0.65));
  projectorGroup.add(indicator);

  // The overhead plate and drop rod remain visible for both centre and side
  // installations, so the side view also communicates its suspended support.
  const ceilingY = state.height;
  const plateThickness = 1.4;
  const projectorTop = lensY + bodyHeight / 2;
  const mountTop = Math.max(projectorTop + 0.5, ceilingY - plateThickness);
  const mountLength = Math.max(0.5, mountTop - projectorTop);
  const mount = new THREE.Mesh(new THREE.CylinderGeometry(cm(0.75), cm(0.75), cm(mountLength), 10), trimMaterial);
  mount.position.set(cm(bodyX), cm(projectorTop + mountLength / 2), cm(lensZ));
  mount.castShadow = true;
  projectorGroup.add(mount);
  addProjectorBox(is4k ? 22 : 16, plateThickness, is4k ? 14 : 10, bodyX, ceilingY - plateThickness / 2, lensZ, trimMaterial, 0.8);
  if (sideMounted) {
    const sideX = bodyX < 0 ? bodyX - bodyWidth / 2 - 2 : bodyX + bodyWidth / 2 + 2;
    const bracket = new THREE.Mesh(new THREE.CylinderGeometry(cm(0.7), cm(0.7), cm(12), 10), trimMaterial);
    bracket.rotation.z = Math.PI / 2;
    bracket.position.set(cm((bodyX + sideX) / 2), cm(lensY), cm(lensZ));
    projectorGroup.add(bracket);
  }

  // The cone is a visual placement aid only. It is shown for both projectors;
  // only the 1080p selector currently exposes a calculated placement range.
  const screenHalfWidth = plan.screenWidth / 2;
  const screenHalfHeight = plan.screenHeight / 2;
  const screenCenterY = plan.frame.height / 2;
  const vertices = new Float32Array([
    cm(bodyX + lensOffsetX), cm(lensY), cm(lensZ - bodyDepth / 2 - 2.1),
    cm(-screenHalfWidth), cm(screenCenterY - screenHalfHeight), cm(plan.screenFrontZ + 0.7),
    cm(screenHalfWidth), cm(screenCenterY - screenHalfHeight), cm(plan.screenFrontZ + 0.7),
    cm(screenHalfWidth), cm(screenCenterY + screenHalfHeight), cm(plan.screenFrontZ + 0.7),
    cm(-screenHalfWidth), cm(screenCenterY + screenHalfHeight), cm(plan.screenFrontZ + 0.7)
  ]);
  const beamGeometry = new THREE.BufferGeometry();
  beamGeometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
  beamGeometry.setIndex([0, 1, 2, 0, 2, 3, 0, 3, 4, 0, 4, 1]);
  const beam = new THREE.Mesh(beamGeometry, projectorBeamMaterial);
  beam.renderOrder = 1;
  projectorGroup.add(beam);
}

function addGroove(parent, width, height, depth, x, y, z) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(cm(width), cm(height), cm(depth)), grooveMaterial);
  mesh.position.set(cm(x), cm(y), cm(z));
  mesh.receiveShadow = true;
  parent.add(mesh);
}

function addTrianglePrism(parent, xStart, length, zPlane) {
  const profile = new THREE.Shape();
  profile.moveTo(0, 0);
  profile.lineTo(0, cm(10));
  profile.lineTo(-cm(15.6), 0);
  profile.closePath();
  const geometry = new THREE.ExtrudeGeometry(profile, {
    depth: cm(Math.max(length - EPP_SEAM, 0.1)),
    bevelEnabled: true,
    bevelThickness: cm(EPP_EDGE_RADIUS),
    bevelSize: cm(EPP_EDGE_RADIUS),
    bevelSegments: 2
  });
  geometry.rotateY(Math.PI / 2);
  const mesh = new THREE.Mesh(geometry, triangleMaterial);
  mesh.position.set(cm(xStart + EPP_SEAM / 2), 0, cm(zPlane));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  parent.add(mesh);

  const seams = new THREE.LineSegments(new THREE.EdgesGeometry(geometry, 18), triangleSeamMaterial);
  seams.position.copy(mesh.position);
  parent.add(seams);
}

function addLModule(parent, side, zCenter) {
  const half = state.frameWidth / 2;
  const seamInset = EPP_SEAM / 2;
  const outer = CORNER_SIZE - EPP_SEAM;
  const leg = EPP_THICKNESS - EPP_SEAM;
  const top = state.frameHeight - seamInset;
  const innerTop = top - leg;
  const bottom = top - outer;
  const leftOuter = -half + seamInset;
  const rightOuter = half - seamInset;
  const profile = new THREE.Shape();

  if (side === 'left') {
    profile.moveTo(cm(leftOuter), cm(top));
    profile.lineTo(cm(leftOuter + outer), cm(top));
    profile.lineTo(cm(leftOuter + outer), cm(innerTop));
    profile.lineTo(cm(leftOuter + leg), cm(innerTop));
    profile.lineTo(cm(leftOuter + leg), cm(bottom));
    profile.lineTo(cm(leftOuter), cm(bottom));
  } else {
    profile.moveTo(cm(rightOuter - outer), cm(top));
    profile.lineTo(cm(rightOuter), cm(top));
    profile.lineTo(cm(rightOuter), cm(bottom));
    profile.lineTo(cm(rightOuter - leg), cm(bottom));
    profile.lineTo(cm(rightOuter - leg), cm(innerTop));
    profile.lineTo(cm(rightOuter - outer), cm(innerTop));
  }
  profile.closePath();

  const depth = EPP_DEPTH - EPP_SEAM;
  const geometry = new THREE.ExtrudeGeometry(profile, {
    depth: cm(depth),
    bevelEnabled: false
  });
  geometry.translate(0, 0, cm(zCenter) - cm(depth) / 2);
  const mesh = new THREE.Mesh(geometry, epp40Material);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  parent.add(mesh);
}

function addLCornerGrooves(parent, side, zCenter) {
  const half = state.frameWidth / 2;
  const sideX = side === 'left' ? -half + EPP_THICKNESS / 2 : half - EPP_THICKNESS / 2;
  const topJoinX = side === 'left' ? -half + CORNER_SIZE : half - CORNER_SIZE;
  addGroove(parent, EPP_THICKNESS + 0.08, 0.2, EPP_DEPTH - EPP_SEAM - 0.2, sideX, state.frameHeight - CORNER_SIZE, zCenter);
  addGroove(parent, 0.2, EPP_THICKNESS + 0.08, EPP_DEPTH - EPP_SEAM - 0.2, topJoinX, state.frameHeight - EPP_THICKNESS / 2, zCenter);
}

function addSegmentRun(parent, segments, axis, fixedA, fixedB, zCenter) {
  let cursor = 0;
  segments.forEach((segment, index) => {
    const material = segment === 50 ? epp50Material : epp40Material;
    if (axis === 'vertical') addBox(parent, EPP_THICKNESS, segment, EPP_DEPTH, fixedA, cursor + segment / 2, zCenter, material, true);
    if (axis === 'top') addBox(parent, segment, EPP_THICKNESS, EPP_DEPTH, fixedA + cursor + segment / 2, fixedB, zCenter, material, true);
    if (axis === 'triangle') addTrianglePrism(parent, fixedA + cursor, segment, fixedB);
    cursor += segment;
    if (index === segments.length - 1) return;
    if (axis === 'vertical') addGroove(parent, EPP_THICKNESS + 0.08, 0.2, EPP_DEPTH - EPP_SEAM - 0.2, fixedA, cursor, zCenter);
    if (axis === 'top') addGroove(parent, 0.2, EPP_THICKNESS + 0.08, EPP_DEPTH - EPP_SEAM - 0.2, fixedA + cursor, fixedB, zCenter);
  });
}

function addGBlockLogo(parent, bottomModuleHeight, zCenter) {
  if (!gBlockLogoTexture) return;
  const aspect = gBlockLogoTexture.image?.width / gBlockLogoTexture.image?.height || 2;
  const logoHeight = Math.min(70 / aspect, Math.max(6, bottomModuleHeight - 6));
  const logoWidth = logoHeight * aspect;
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(cm(logoWidth), cm(logoHeight)), gBlockLogoMaterial);
  mesh.rotation.y = Math.PI / 2;
  mesh.position.set(
    cm(-state.frameWidth / 2 + EPP_THICKNESS + 0.06),
    cm(bottomModuleHeight / 2),
    cm(zCenter)
  );
  mesh.renderOrder = 4;
  parent.add(mesh);
}

function addLayerSeam(parent, zPlane) {
  const half = state.frameWidth / 2;
  const xLeft = -half + EPP_THICKNESS;
  const xRight = half - EPP_THICKNESS;
  const yTop = state.frameHeight - EPP_THICKNESS;
  const vertices = new Float32Array([
    cm(xLeft), 0, cm(zPlane), cm(xLeft), cm(yTop), cm(zPlane),
    cm(xRight), 0, cm(zPlane), cm(xRight), cm(yTop), cm(zPlane),
    cm(xLeft), cm(yTop), cm(zPlane), cm(xRight), cm(yTop), cm(zPlane)
  ]);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
  const seam = new THREE.LineSegments(geometry, layerSeamMaterial);
  seam.renderOrder = 2;
  parent.add(seam);
}

function buildAssembly() {
  clearAssembly();
  if (isGCurve()) return;
  const plan = buildPlan();
  if (!plan || !state.selectedGBlock) return;
  const half = state.frameWidth / 2;

  for (let layerIndex = 0; layerIndex < plan.layers; layerIndex += 1) {
    const zPlane = -layerIndex * EPP_DEPTH;
    const zCenter = zPlane - EPP_DEPTH / 2;
    addLModule(assembly, 'left', zCenter);
    addLModule(assembly, 'right', zCenter);
    addLCornerGrooves(assembly, 'left', zCenter);
    addLCornerGrooves(assembly, 'right', zCenter);
    addSegmentRun(assembly, plan.vertical, 'vertical', -half + EPP_THICKNESS / 2, null, zCenter);
    if (layerIndex === 0) addGBlockLogo(assembly, plan.vertical[0], zCenter);
    addSegmentRun(assembly, plan.vertical, 'vertical', half - EPP_THICKNESS / 2, null, zCenter);
    addSegmentRun(assembly, plan.top, 'top', -half + CORNER_SIZE, state.frameHeight - EPP_THICKNESS / 2, zCenter);
  }

  // Triangles are a single rear support row: flush with the screen-facing EPP plane.
  addSegmentRun(assembly, plan.bottom, 'triangle', -half + EPP_THICKNESS, -frameDepth() + EPP_SEAM / 2);
  if (plan.layers === 2) addLayerSeam(assembly, -EPP_DEPTH);

  // The screen sits directly against the rear of the deepest EPP layer.
  addBox(
    assembly,
    state.frameWidth - 12,
    state.frameHeight - 12,
    SCREEN_THICKNESS,
    0,
    state.frameHeight / 2,
    -frameDepth() + EPP_SEAM / 2 - SCREEN_THICKNESS / 2,
    screenMaterial
  );
}

// =========================
// 02 G-Curve enclosure
// External frame: 480 W × 220 D × 280 H cm. The screen is a symmetric
// concave arc: its two edge points are 107 cm behind the opening and its
// centre is 190 cm behind the opening.
// =========================
function gCurveScreenDepth(x) {
  const halfWidth = GCURVE.width / 2;
  const sagitta = GCURVE.screenCentreDepth - GCURVE.screenSideDepth;
  const radius = (halfWidth * halfWidth + sagitta * sagitta) / (2 * sagitta);
  const circleCentreDepth = GCURVE.screenCentreDepth - radius;
  return circleCentreDepth + Math.sqrt(Math.max(0, radius * radius - x * x));
}
function addGCurveTube(parent, points, radius = 2.3) {
  const curve = new THREE.CatmullRomCurve3(points.map((point) => new THREE.Vector3(cm(point.x), cm(point.y), cm(point.z))), false, 'centripetal');
  const mesh = new THREE.Mesh(new THREE.TubeGeometry(curve, Math.max(12, points.length * 3), cm(radius), 8, false), gCurvePipeMaterial);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  parent.add(mesh);
}
function addGCurveBottomTrim(parent, arcPoints) {
  const positions = [];
  const indices = [];
  const trimHeight = 8;
  arcPoints.forEach((point) => {
    const z = point.z + 0.8;
    positions.push(cm(point.x), 0, cm(z), cm(point.x), cm(trimHeight), cm(z));
  });
  for (let index = 0; index < arcPoints.length - 1; index += 1) {
    const lowerLeft = index * 2;
    const upperLeft = lowerLeft + 1;
    const lowerRight = lowerLeft + 2;
    const upperRight = lowerLeft + 3;
    indices.push(lowerLeft, lowerRight, upperLeft, upperLeft, lowerRight, upperRight);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  const trim = new THREE.Mesh(geometry, gCurveFabricMaterial);
  trim.renderOrder = 2;
  parent.add(trim);
}
function addGCurveSideLogo(parent) {
  if (!gCurveLogoTexture) return;
  const aspect = gCurveLogoTexture.image?.width / gCurveLogoTexture.image?.height || 4;
  const logoWidth = 100;
  const logoHeight = Math.min(36, logoWidth / aspect);
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(cm(logoWidth), cm(logoHeight)), gCurveLogoMaterial);
  // Keep the decal inside the clear front return zone (0–107 cm), clear of the curved screen edge.
  mesh.rotation.y = Math.PI / 2;
  mesh.position.set(cm(-GCURVE.width / 2 + 3.15), cm(48), cm(-52));
  mesh.renderOrder = 4;
  parent.add(mesh);
}
function buildGCurveAssembly() {
  clearAssembly();
  if (!state.selectedGBlock) return;
  const halfWidth = GCURVE.width / 2;
  const back = -GCURVE.depth;
  const front = 0;
  const height = GCURVE.height;
  const screenSegments = 32;

  // Square external steel-tube frame: front and rear uprights, top rails, and floor rails.
  [-halfWidth, halfWidth].forEach((x) => {
    addGCurveTube(assembly, [{ x, y: 0, z: front }, { x, y: height, z: front }]);
    addGCurveTube(assembly, [{ x, y: 0, z: back }, { x, y: height, z: back }]);
    addGCurveTube(assembly, [{ x, y: 3, z: front }, { x, y: 3, z: back }], 2);
    addGCurveTube(assembly, [{ x, y: height, z: front }, { x, y: height, z: back }], 2);
  });
  addGCurveTube(assembly, [{ x: -halfWidth, y: height, z: front }, { x: halfWidth, y: height, z: front }]);
  addGCurveTube(assembly, [{ x: -halfWidth, y: height, z: back }, { x: halfWidth, y: height, z: back }]);
  addGCurveTube(assembly, [{ x: -halfWidth, y: 3, z: back }, { x: halfWidth, y: 3, z: back }], 2);

  // Black fabric enclosure: two straight side walls and a flat ceiling contained by the outer square frame.
  addBox(assembly, 3, height - 5, GCURVE.depth, -halfWidth + 1.5, height / 2, (front + back) / 2, gCurveFabricMaterial);
  addBox(assembly, 3, height - 5, GCURVE.depth, halfWidth - 1.5, height / 2, (front + back) / 2, gCurveFabricMaterial);
  addBox(assembly, GCURVE.width - 4, 3, GCURVE.depth, 0, height - 1.5, (front + back) / 2, gCurveFabricMaterial);
  addGCurveSideLogo(assembly);

  // Curved screen mesh: 32 vertical panels form one continuous, symmetric concave surface.
  const positions = [];
  const indices = [];
  for (let index = 0; index <= screenSegments; index += 1) {
    const x = -halfWidth + (GCURVE.width * index) / screenSegments;
    const z = -gCurveScreenDepth(x);
    positions.push(cm(x), 0, cm(z), cm(x), cm(height), cm(z));
  }
  for (let index = 0; index < screenSegments; index += 1) {
    const lowerLeft = index * 2;
    const upperLeft = lowerLeft + 1;
    const lowerRight = lowerLeft + 2;
    const upperRight = lowerLeft + 3;
    indices.push(lowerLeft, lowerRight, upperLeft, upperLeft, lowerRight, upperRight);
  }
  const screenGeometry = new THREE.BufferGeometry();
  screenGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  screenGeometry.setIndex(indices);
  screenGeometry.computeVertexNormals();
  const screen = new THREE.Mesh(screenGeometry, screenMaterial);
  screen.castShadow = true;
  screen.receiveShadow = true;
  assembly.add(screen);

  // Black curved steel rails define the screen edge and make the screen shape readable from every angle.
  const arcPoints = Array.from({ length: screenSegments + 1 }, (_, index) => {
    const x = -halfWidth + (GCURVE.width * index) / screenSegments;
    return { x, y: 3, z: -gCurveScreenDepth(x) };
  });
  addGCurveBottomTrim(assembly, arcPoints);
  addGCurveTube(assembly, arcPoints, 2.5);
  addGCurveTube(assembly, arcPoints.map((point) => ({ ...point, y: height - 3 })), 2.5);
  addGCurveTube(assembly, [{ x: -halfWidth, y: 3, z: -GCURVE.screenSideDepth }, { x: -halfWidth, y: height - 3, z: -GCURVE.screenSideDepth }], 2.5);
  addGCurveTube(assembly, [{ x: halfWidth, y: 3, z: -GCURVE.screenSideDepth }, { x: halfWidth, y: height - 3, z: -GCURVE.screenSideDepth }], 2.5);
}

function setDefaultCamera(width, height, depth) {
  const mobileView = window.matchMedia('(max-width: 900px)').matches;
  camera.position.set(0, height * 0.5, depth / 2 + (mobileView ? 4.5 : 6.5));
  controls.target.set(0, height * 0.5, -depth * 0.18);
  controls.update();
}

const golferMaterials = {
  skin: new THREE.MeshStandardMaterial({ color: 0xc99878, roughness: 0.86 }),
  shirt: new THREE.MeshStandardMaterial({ color: 0x315f48, roughness: 0.82 }),
  pants: new THREE.MeshStandardMaterial({ color: 0x29322d, roughness: 0.9 }),
  shoes: new THREE.MeshStandardMaterial({ color: 0xe8e5dd, roughness: 0.74 }),
  sole: new THREE.MeshStandardMaterial({ color: 0x252925, roughness: 0.93 }),
  glove: new THREE.MeshStandardMaterial({ color: 0xf0eee6, roughness: 0.8 }),
  club: new THREE.MeshStandardMaterial({ color: 0xc5c9c5, roughness: 0.35, metalness: 0.62 }),
  grip: new THREE.MeshStandardMaterial({ color: 0x202522, roughness: 0.92 }),
  clubHead: new THREE.MeshStandardMaterial({ color: 0x303633, roughness: 0.44, metalness: 0.38 }),
  ball: new THREE.MeshStandardMaterial({ color: 0xf8f7f1, roughness: 0.68 }),
  swingSafe: new THREE.MeshBasicMaterial({ color: 0x3d8a5d, transparent: true, opacity: 0.22, depthWrite: false }),
  swingLimited: new THREE.MeshBasicMaterial({ color: 0xbb7b27, transparent: true, opacity: 0.25, depthWrite: false }),
  swingRisk: new THREE.MeshBasicMaterial({ color: 0xb24e47, transparent: true, opacity: 0.28, depthWrite: false })
};

function clearGolfer() {
  while (golferGroup.children.length) {
    const child = golferGroup.children[0];
    golferGroup.remove(child);
    child.traverse?.((mesh) => { mesh.geometry?.dispose?.(); });
  }
}

function clubLength() { return state.club === 'Iron' ? 94 : 114; }
function ballPositionZ() { return primaryTeePosition().z; }
const SWING_CONTACT_MARGIN = 5;
const SWING_SAFE_CLEARANCE = 30;

function cmVector(x, y, z) { return new THREE.Vector3(cm(x), cm(y), cm(z)); }

function getBackswingPose() {
  const height = state.golferHeight;
  const side = state.handedness === 'right' ? -1 : 1;
  const tee = primaryTeePosition();
  const ballZ = tee.z;
  // Human mass changes mostly in the vertical direction. Width and depth grow
  // more slowly, while the club remains its independent 94/114 cm dimension.
  const bodyWidthScale = 1 + ((height - 175) / 175) * 0.3;
  const bodyDepthScale = 1 + ((height - 175) / 175) * 0.2;
  const hip = new THREE.Vector3(tee.x, height * 0.49, ballZ + 8 * bodyDepthScale);
  const shoulder = new THREE.Vector3(tee.x + side * height * 0.025 * bodyWidthScale, height * 0.75, ballZ + bodyDepthScale);
  const hands = new THREE.Vector3(tee.x + side * height * 0.15 * bodyWidthScale, height * 0.63, ballZ + 7 * bodyDepthScale);
  const clubDirection = new THREE.Vector3(side * 0.66, 0.66, 0.35).normalize();
  const clubHead = hands.clone().addScaledVector(clubDirection, clubLength());
  return { height, side, ballZ, hip, shoulder, hands, clubDirection, clubHead, bodyWidthScale, bodyDepthScale };
}

function swingClearance() {
  const pose = getBackswingPose();
  const minX = Math.min(pose.hands.x, pose.clubHead.x) - SWING_CONTACT_MARGIN;
  const maxX = Math.max(pose.hands.x, pose.clubHead.x) + SWING_CONTACT_MARGIN;
  const minY = Math.min(pose.hands.y, pose.clubHead.y) - SWING_CONTACT_MARGIN;
  const maxY = Math.max(pose.hands.y, pose.clubHead.y) + SWING_CONTACT_MARGIN;
  const minZ = Math.min(pose.hands.z, pose.clubHead.z) - SWING_CONTACT_MARGIN;
  const maxZ = Math.max(pose.hands.z, pose.clubHead.z) + SWING_CONTACT_MARGIN;
  return [
    { label: 'left wall', value: minX + state.width / 2 },
    { label: 'right wall', value: state.width / 2 - maxX },
    { label: 'rear wall / screen', value: minZ + state.depth / 2 },
    { label: 'front wall', value: state.depth / 2 - maxZ },
    { label: 'ceiling', value: state.height - maxY }
  ];
}

function renderSwingResult() {
  const result = document.getElementById('swing-result');
  if (!result) return;
  const values = swingClearance();
  const collision = values.filter((item) => item.value < 0);
  const limited = values.filter((item) => item.value >= 0 && item.value < SWING_SAFE_CLEARANCE);
  result.className = 'gebblo-planner__swing-result';
  const title = document.getElementById('swing-space-title');
  if (title) title.textContent = `${state.club} top-of-backswing clearance`;
  if (collision.length) {
    result.classList.add('is-risk');
    result.innerHTML = `<strong>Collision risk</strong><span class="gebblo-planner__swing-detail">${collision.map((item) => `${item.label} overlaps by ${Math.round(Math.abs(item.value))} cm`).join(' · ')}</span>`;
  } else if (limited.length) {
    result.classList.add('is-limited');
    result.innerHTML = `<strong>Limited clearance</strong><span class="gebblo-planner__swing-detail">Keep at least 30 cm clear. ${limited.map((item) => `${item.label}: ${Math.round(item.value)} cm`).join(' · ')}</span>`;
  } else {
    result.innerHTML = '<strong>Clearance OK</strong><span class="gebblo-planner__swing-detail">The club’s top-of-backswing position keeps at least 30 cm clear.</span>';
  }
}

function addGolferCylinder(parent, radius, height, x, y, z, material) {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(cm(radius), cm(radius), cm(height), 10), material);
  mesh.position.set(cm(x), cm(y + height / 2), cm(z));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  parent.add(mesh);
  return mesh;
}

function addLimb(parent, start, end, radius, material, segments = 8) {
  const startWorld = cmVector(start.x, start.y, start.z);
  const endWorld = cmVector(end.x, end.y, end.z);
  const direction = new THREE.Vector3().subVectors(endWorld, startWorld);
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(cm(radius * 0.88), cm(radius), direction.length(), segments), material);
  mesh.position.copy(startWorld).add(endWorld).multiplyScalar(0.5);
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
  mesh.castShadow = true; mesh.receiveShadow = true; parent.add(mesh);
  return mesh;
}

function addRoundedPart(parent, width, height, depth, position, material, radius = 2) {
  const mesh = new THREE.Mesh(new RoundedBoxGeometry(cm(width), cm(height), cm(depth), 3, cm(radius)), material);
  mesh.position.copy(cmVector(position.x, position.y, position.z));
  mesh.castShadow = true; mesh.receiveShadow = true; parent.add(mesh);
  return mesh;
}

function buildGolfer() {
  clearGolfer();
  const pose = getBackswingPose();
  const { height, side, ballZ, hip, shoulder, hands, clubDirection, clubHead } = pose;
  golferGroup.visible = state.step === 'room';
  const stance = height * 0.17;
  const leadFoot = new THREE.Vector3(hip.x - stance / 2, 5, ballZ + 11 * pose.bodyDepthScale);
  const trailFoot = new THREE.Vector3(hip.x + stance / 2, 5, ballZ - 7 * pose.bodyDepthScale);
  const leadKnee = new THREE.Vector3(hip.x - stance * 0.33, height * 0.25, ballZ + 4 * pose.bodyDepthScale);
  const trailKnee = new THREE.Vector3(hip.x + stance * 0.26, height * 0.28, ballZ - 2 * pose.bodyDepthScale);
  const hips = new THREE.Vector3(hip.x, hip.y, hip.z);
  const torsoBase = new THREE.Vector3(hips.x, hips.y - height * 0.08, hips.z);
  const neck = new THREE.Vector3(shoulder.x, height * 0.84, ballZ - 5);
  const headCenter = new THREE.Vector3(hip.x + side * height * 0.035, height * 0.91, ballZ - 10 * pose.bodyDepthScale);
  addRoundedPart(golferGroup, height * 0.125, height * 0.06, height * 0.21, leadFoot, golferMaterials.shoes, 2).rotation.y = THREE.MathUtils.degToRad(-8);
  addRoundedPart(golferGroup, height * 0.125, height * 0.06, height * 0.21, trailFoot, golferMaterials.shoes, 2).rotation.y = THREE.MathUtils.degToRad(9);
  addLimb(golferGroup, leadFoot.clone().setY(8), leadKnee, height * 0.044, golferMaterials.pants);
  addLimb(golferGroup, leadKnee, hips.clone().add(new THREE.Vector3(-height * 0.025, 0, 0)), height * 0.052, golferMaterials.pants);
  addLimb(golferGroup, trailFoot.clone().setY(8), trailKnee, height * 0.044, golferMaterials.pants);
  addLimb(golferGroup, trailKnee, hips.clone().add(new THREE.Vector3(height * 0.025, 0, 0)), height * 0.052, golferMaterials.pants);
  addRoundedPart(golferGroup, height * 0.18, height * 0.1, height * 0.16, hips, golferMaterials.pants, 3);
  const torso = addLimb(golferGroup, torsoBase, shoulder, height * 0.11, golferMaterials.shirt, 10);
  torso.scale.x = 1.18;
  addGolferCylinder(golferGroup, height * 0.036, height * 0.07, neck.x, neck.y - height * 0.035, neck.z, golferMaterials.skin, 10);
  const head = new THREE.Mesh(new THREE.SphereGeometry(cm(height * 0.068), 16, 12), golferMaterials.skin);
  head.position.copy(cmVector(headCenter.x, headCenter.y, headCenter.z)); head.castShadow = true; head.receiveShadow = true; golferGroup.add(head);
  const cap = new THREE.Mesh(new THREE.SphereGeometry(cm(height * 0.07), 16, 8, 0, Math.PI * 2, 0, Math.PI / 2), golferMaterials.pants);
  cap.position.copy(cmVector(headCenter.x, headCenter.y + height * 0.025, headCenter.z)); cap.rotation.z = side * THREE.MathUtils.degToRad(8); golferGroup.add(cap);
  const leadShoulder = shoulder.clone().add(new THREE.Vector3(-height * 0.095 * pose.bodyWidthScale, -height * 0.005, 0));
  const trailShoulder = shoulder.clone().add(new THREE.Vector3(height * 0.095 * pose.bodyWidthScale, height * 0.015, 0));
  const leadElbow = new THREE.Vector3(hip.x + side * height * 0.08 * pose.bodyWidthScale, height * 0.70, ballZ + 4 * pose.bodyDepthScale);
  const trailElbow = new THREE.Vector3(hip.x + side * height * 0.28 * pose.bodyWidthScale, height * 0.74, ballZ + 14 * pose.bodyDepthScale);
  const leadHand = hands.clone().add(new THREE.Vector3(-side * 2.5, 1.5, -1));
  const trailHand = hands.clone().add(new THREE.Vector3(side * 2.5, -1.5, 1));
  addLimb(golferGroup, leadShoulder, leadElbow, height * 0.036, golferMaterials.shirt);
  addLimb(golferGroup, leadElbow, leadHand, height * 0.029, golferMaterials.skin);
  addLimb(golferGroup, trailShoulder, trailElbow, height * 0.038, golferMaterials.shirt);
  addLimb(golferGroup, trailElbow, trailHand, height * 0.03, golferMaterials.skin);
  [leadHand, trailHand].forEach((hand) => {
    const glove = new THREE.Mesh(new THREE.SphereGeometry(cm(height * 0.027), 10, 8), golferMaterials.glove);
    glove.position.copy(cmVector(hand.x, hand.y, hand.z)); glove.castShadow = true; golferGroup.add(glove);
  });
  addLimb(golferGroup, hands, clubHead, 1.15, golferMaterials.club, 8);
  const gripEnd = hands.clone().addScaledVector(clubDirection, 17);
  addLimb(golferGroup, hands.clone().addScaledVector(clubDirection, -5), gripEnd, 1.72, golferMaterials.grip, 8);
  const clubHeadMesh = addRoundedPart(golferGroup, state.club === 'Iron' ? 8 : 10, 4, state.club === 'Iron' ? 3 : 5, clubHead, golferMaterials.clubHead, 1);
  clubHeadMesh.quaternion.setFromUnitVectors(new THREE.Vector3(1, 0, 0), clubDirection.clone().normalize());
  const ball = new THREE.Mesh(new THREE.SphereGeometry(cm(2.2), 10, 8), golferMaterials.ball);
  ball.position.copy(cmVector(hip.x, 2.5, ballZ - 19)); ball.castShadow = true; golferGroup.add(ball);
  const values = swingClearance();
  const material = values.some((item) => item.value < 0) ? golferMaterials.swingRisk : values.some((item) => item.value < 30) ? golferMaterials.swingLimited : golferMaterials.swingSafe;
  const swingEnvelope = addLimb(golferGroup, hands, clubHead, 3.4, material, 10);
  swingEnvelope.renderOrder = 3;
  const envelope = new THREE.Mesh(new THREE.SphereGeometry(cm(7), 14, 10), material);
  envelope.position.copy(cmVector(clubHead.x, clubHead.y, clubHead.z)); envelope.renderOrder = 3; golferGroup.add(envelope);
  const guide = new THREE.Line(new THREE.BufferGeometry().setFromPoints([
    cmVector(hands.x, hands.y, hands.z), cmVector(clubHead.x, clubHead.y, clubHead.z)
  ]), new THREE.LineBasicMaterial({ color: material.color, transparent: true, opacity: 0.82 }));
  guide.renderOrder = 4; golferGroup.add(guide);
}

// Static GLB golfer used only as a room-scale reference. The model is measured
// after loading, so the height slider remains accurate even if the Blender file
// was exported using a different unit scale.
function loadExternalGolfer() {
  if (golferLoadStarted) return;
  golferLoadStarted = true;
  const loader = new GLTFLoader();
  const finishGolferLoad = (gltf) => {
    externalGolfer = gltf.scene;
    externalGolfer.traverse((node) => {
      if (!node.isMesh) return;
      node.castShadow = true;
      node.receiveShadow = true;
      if (node.material) node.material.needsUpdate = true;
      // When a source GLB keeps the club in a separate named mesh, retain the
      // club's physical dimensions while the body is adjusted for height.
      if (/(club|shaft|grip|driver|iron|clubhead)/i.test(node.name || '')) {
        node.userData.gebbloFixedClub = true;
        node.userData.gebbloBaseScale = node.scale.clone();
      }
    });
    golferGroup.add(externalGolfer);
    positionExternalGolfer();
    syncUI();
  };
  const failGolferLoad = (error) => {
    // Keep the browser console useful when an asset was not uploaded to the
    // same theme, or Shopify returns an error page instead of the GLB binary.
    console.error('GEBBLO golfer model could not be loaded:', error);
    golferLoadFailed = true;
    golferLoadMessage = error?.message || 'The theme asset could not be read';
    syncUI();
  };

  // Shopify's theme-asset pipeline can alter binary .glb responses in the
  // editor preview. The model is therefore delivered as a normal JavaScript
  // asset, loaded before this module in the section file.
  const embeddedModel = window.GEBBLO_GOLFER_GLB_BASE64;
  if (!embeddedModel) {
    failGolferLoad(new Error('The embedded golfer asset was not loaded'));
    return;
  }
  try {
    const binary = atob(embeddedModel);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    loader.parse(bytes.buffer, '', finishGolferLoad, failGolferLoad);
  } catch (error) {
    failGolferLoad(error);
  }
}

function positionExternalGolfer() {
  // A selected golfer is a spatial reference for the whole configuration,
  // not just the first room step. It therefore stays visible while products
  // are added and in the final review.
  golferGroup.visible = state.golferVisible;
  if (!state.golferVisible) return;
  if (!externalGolfer) { loadExternalGolfer(); return; }

  // Reset first, then derive a true bounding-box height from the imported GLB.
  externalGolfer.position.set(0, 0, 0);
  externalGolfer.rotation.set(0, Math.PI, 0);
  externalGolfer.scale.set(1, 1, 1);
  // Restore the imported club mesh scales before each measurement. The club
  // receives an inverse scale below to preserve its real length while the body
  // height changes; without this reset it compounds on every slider update.
  externalGolfer.traverse((node) => {
    if (node.userData?.gebbloFixedClub && node.userData.gebbloBaseScale) {
      node.scale.copy(node.userData.gebbloBaseScale);
    }
  });
  let bounds = new THREE.Box3().setFromObject(externalGolfer);
  const nativeHeight = bounds.getSize(new THREE.Vector3()).y;
  const targetHeight = cm(state.golferHeight);
  const baseScale = nativeHeight > 0.001 ? cm(GOLFER_REFERENCE_HEIGHT) / (nativeHeight * GOLFER_MODEL_BODY_HEIGHT_RATIO) : 1;
  const heightScale = nativeHeight > 0.001 ? targetHeight / (nativeHeight * GOLFER_MODEL_BODY_HEIGHT_RATIO) : 1;
  // A person getting taller does not become proportionally wider/deeper. Keep
  // the width/depth response deliberately gentle: +/- 35 cm in height changes
  // the silhouette by about six percent, while the vertical dimension follows
  // the selected height exactly.
  const statureOffset = (state.golferHeight - GOLFER_REFERENCE_HEIGHT) / GOLFER_REFERENCE_HEIGHT;
  const widthScale = baseScale * (1 + statureOffset * 0.3);
  const depthScale = baseScale * (1 + statureOffset * 0.2);
  externalGolfer.scale.set(widthScale, heightScale, depthScale);
  externalGolfer.traverse((node) => {
    if (!node.userData?.gebbloFixedClub || !node.userData.gebbloBaseScale) return;
    const baseScale = node.userData.gebbloBaseScale;
    node.scale.set(baseScale.x / widthScale, baseScale.y / heightScale, baseScale.z / depthScale);
  });
  bounds = new THREE.Box3().setFromObject(externalGolfer);

  const floorY = cm(FLOOR_TOP + (turfIsSelected() ? TURF_THICKNESS : 0));
  const tee = primaryTeePosition();
  // Stand directly behind the ball, looking toward the rear wall / screen.
  // Positive Z is toward the open front of the room in this planner.
  const stanceBehindBall = cm(GOLFER_STANCE_BEHIND_TEE);
  externalGolfer.position.x += -(bounds.min.x + bounds.max.x) / 2;
  externalGolfer.position.y += floorY - bounds.min.y;
  externalGolfer.position.x += cm(tee.x);
  externalGolfer.position.z += cm(tee.z) + stanceBehindBall - (bounds.min.z + bounds.max.z) / 2;
}

function updateRoom(resetCamera = false) {
  const width = cm(state.width);
  const height = cm(state.height);
  const depth = cm(state.depth);
  // The floor top is the room's y = 0 datum.  Each wall is a single visual
  // surface and the surfaces share only their boundary edges: no white seams
  // and no overlapping faces at the room corners.
  floor.scale.set(width, 1, depth);
  floor.position.set(0, -cm(FLOOR_THICKNESS) / 2, 0);
  backWall.scale.set(width, height, 1);
  backWall.position.set(0, height / 2, -depth / 2);
  leftWall.scale.set(depth, height, 1);
  leftWall.position.set(-width / 2, height / 2, 0);
  rightWall.scale.set(depth, height, 1);
  rightWall.position.set(width / 2, height / 2, 0);
  ceiling.scale.set(width, depth, 1);
  ceiling.position.set(0, height, 0);
  const assemblyDepth = isGCurve() ? GCURVE.depth : frameDepth();
  assembly.position.set(0, turfIsSelected() ? cm(FLOOR_TOP + TURF_THICKNESS) : 0, -depth / 2 + cm(REAR_CLEARANCE + assemblyDepth + SCREEN_THICKNESS));
  assembly.visible = state.selectedGBlock && frameFits();
  if (isGCurve()) buildGCurveAssembly();
  else buildAssembly();
  buildTurf();
  buildHittingMat();
  buildLaunchMonitor();
  buildProjector();
  positionExternalGolfer();
  controls.minDistance = Math.max(3.5, Math.min(width, depth) * 0.55);
  controls.maxDistance = Math.max(16, Math.max(width, depth) * 2.7);
  if (resetCamera) setDefaultCamera(width, height, depth);
  savePlannerState();
}

function fadeWall(mesh, targetOpacity) {
  if (targetOpacity > 0.05 && !mesh.visible) mesh.visible = true;
  mesh.material.opacity += (targetOpacity - mesh.material.opacity) * 0.14;
  mesh.material.depthWrite = mesh.material.opacity > 0.18;
  if (targetOpacity <= 0.05 && mesh.material.opacity < 0.06) mesh.visible = false;
}

function updateOccludingWalls() {
  cameraOffset.subVectors(camera.position, controls.target);
  const horizontalDistance = Math.hypot(cameraOffset.x, cameraOffset.z);
  if (horizontalDistance < 0.01) return;

  // OrbitControls polar angle is reliable regardless of the camera's zoom distance.
  // A view above roughly 72 degrees from horizontal is treated as an overhead inspection view.
  const overheadView = controls.getPolarAngle() < THREE.MathUtils.degToRad(72);
  // Fade only the side wall that is between the camera and the room.
  // Once a side wall is between the camera and the room, keep it faded even while
  // looking upward; otherwise it would reappear and block the frame/projector detail.
  const sideFadeThreshold = horizontalDistance * 0.33;
  fadeWall(leftWall, cameraOffset.x < -sideFadeThreshold ? 0.04 : 1);
  fadeWall(rightWall, cameraOffset.x > sideFadeThreshold ? 0.04 : 1);
  // Fade the rear wall only when the camera moves behind the screen wall.
  // Other views retain it so the customer can still read the room shape.
  const rearFadeThreshold = horizontalDistance * 0.33;
  fadeWall(backWall, cameraOffset.z < -rearFadeThreshold ? 0.04 : 1);
  fadeWall(ceiling, overheadView ? 0.04 : 1);
}

function renderFit() {
  // Frame size limits are handled directly by the sliders; support lives in the shared preview link.
}

function renderProjectorGuidance() {
  const guidance = document.getElementById('projector-guidance');
  if (!guidance) return;
  const plan = projectorPlan();
  const visible = state.step === 'projector' && projectorIsSelected() && plan;
  guidance.hidden = !visible;
  if (!visible) return;
  const warnings = projectorWarnings(plan);
  const note = document.getElementById('projector-guidance-note');
  const distanceInput = document.getElementById('projector-distance-range');
  const heightInput = document.getElementById('projector-height-range');
  const distanceOutput = document.getElementById('projector-distance-value');
  const heightOutput = document.getElementById('projector-height-value');
  const heightLabel = document.querySelector('label[for="projector-height-range"]');
  enforceProjectorMountEligibility();
  const canUseCeilingMount = ceilingMountAllowed();
  document.querySelectorAll('[data-projector-mount]').forEach((button) => {
    const ceilingUnavailable = button.dataset.projectorMount === 'center' && !canUseCeilingMount;
    button.disabled = ceilingUnavailable;
    button.classList.toggle('is-disabled', ceilingUnavailable);
    button.classList.toggle('is-active', button.dataset.projectorMount === state.projectorMount);
  });
  const mountNote = document.getElementById('projector-mount-note');
  mountNote.hidden = canUseCeilingMount;
  heightLabel.textContent = 'Projector height';
  distanceInput.min = Math.ceil(Math.min(plan.minDistance, plan.safeMaxDistance));
  distanceInput.max = Math.floor(Math.max(plan.minDistance, plan.safeMaxDistance));
  distanceInput.value = Math.round(plan.mountDistance);
  distanceInput.disabled = !plan.depthFits;
  heightInput.min = Math.ceil(plan.minLensHeight);
  heightInput.max = Math.floor(Math.max(plan.minLensHeight, plan.maxLensHeight));
  heightInput.value = Math.round(plan.lensHeight);
  heightInput.disabled = !plan.heightFits;
  distanceOutput.textContent = `${Math.round(plan.mountDistance)} cm (${(plan.mountDistance / 2.54).toFixed(1)} in)`;
  heightOutput.textContent = `${Math.round(plan.lensHeight)} cm (${(plan.lensHeight / 2.54).toFixed(1)} in)`;
  const normalRange = `${Math.ceil(plan.minDistance)}–${Math.floor(plan.maxDistance)} cm`;
  const availableRange = plan.depthFits ? `${Math.ceil(plan.minDistance)}–${Math.floor(plan.safeMaxDistance)} cm` : `up to ${Math.floor(plan.safeMaxDistance)} cm`;
  note.innerHTML = warnings.length
    ? `${warnings.map((warning) => `<span>${warning}</span>`).join('')}<span>Normal range: ${normalRange}. Available in this room: ${availableRange}.</span>`
    : `<span>Normal installation range: ${normalRange} from the screen.</span><span>Projector height: ${Math.ceil(plan.minLensHeight)}–${Math.floor(plan.maxLensHeight)} cm. ${plan.sideMounted ? 'Side mount adds 50 cm to the center-mount distance.' : 'Center mount is centred on the frame.'}</span>`;
  guidance.classList.toggle('is-warning', warnings.length > 0);
}

function renderReview() {
  const summary = document.getElementById('configuration-summary');
  summary.replaceChildren();
  const addRow = (label, value) => { const row = document.createElement('div'); row.className = 'gebblo-planner__review-row'; const key = document.createElement('span'); key.textContent = label; const detail = document.createElement('strong'); detail.textContent = value; row.append(key, detail); summary.append(row); };
  const framePrice = selectedVariant?.price ? ` · ${money(selectedVariant.price.amount, selectedVariant.price.currencyCode)}` : '';
  addRow('Frame', state.selectedGBlock ? (isGCurve() ? `${selectedFrameProduct?.title || 'G-Curve'} · ${GCURVE.depth} cm D × ${GCURVE.width} cm W × ${GCURVE.height} cm H${framePrice}` : `${selectedFrameProduct?.title || 'G-Block'} · ${state.layer} · ${frameDepth()} cm D × ${state.frameWidth} cm W × ${state.frameHeight} cm H${framePrice}`) : 'Not selected');
  ['turf', 'mat', 'monitor', 'projector'].forEach((step) => { const item = state.accessories[step]; const accessory = ACCESSORIES[step]; const price = item.variant?.price ? ` · ${money(item.variant.price.amount, item.variant.price.currencyCode)}` : ''; const detail = item.selected ? `${item.title || accessory.name}${price}` : 'Not added'; addRow(accessory.name, detail); });
  syncTotal();
}

function validConfiguration() {
  return state.selectedGBlock && (isGCurve() ? frameFits() : Boolean(buildPlan()));
}
function money(amount, currency) {
  // Use a plain dollar sign throughout the planner instead of locale-specific
  // labels such as "US$" when the storefront language is not English.
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', currencyDisplay: 'narrowSymbol' }).format(Number(amount));
}
function setProductCardPrice(handle, price, from = false) {
  if (!handle || !price?.amount || !price?.currencyCode) return;
  document.querySelectorAll(`[data-product-card][data-product-handle="${CSS.escape(handle)}"] [data-product-price]`).forEach((element) => {
    element.textContent = `${from ? 'From ' : ''}${money(price.amount, price.currencyCode)}`;
    if (from) delete element.dataset.priceExact;
    else element.dataset.priceExact = 'true';
    element.hidden = false;
  });
}
function setSelectedPrice(id, price) {
  const element = document.getElementById(id);
  if (!element) return;
  element.hidden = !price?.amount || !price?.currencyCode;
  if (!element.hidden) element.textContent = money(price.amount, price.currencyCode);
}
function syncTotal() {
  const prices = [];
  if (selectedVariant?.price) prices.push(selectedVariant.price);
  Object.values(state.accessories).forEach((selection) => { if (selection?.selected && selection.variant?.price) prices.push(selection.variant.price); });
  const total = prices.reduce((sum, price) => sum + Number(price.amount || 0), 0);
  const currency = prices[0]?.currencyCode;
  document.querySelectorAll('[data-total-value]').forEach((element) => { element.textContent = currency ? money(total, currency) : '—'; });
}
async function loadProductCardPrices() {
  if (!storefrontToken) return;
  const handles = [...new Set([...document.querySelectorAll('[data-product-card]')].map((card) => card.dataset.productHandle).filter(Boolean))];
  const query = 'query PlannerCardPrice($handle:String!){product(handle:$handle){variants(first:100){nodes{availableForSale price{amount currencyCode}}}}}';
  await Promise.all(handles.map(async (handle) => {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'api/2026-07/graphql.json', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': storefrontToken }, body: JSON.stringify({ query, variables: { handle } }) });
      const variants = (await response.json()).data?.product?.variants?.nodes || [];
      const sellable = variants.filter((variant) => variant.availableForSale && variant.price?.amount);
      const options = sellable.length ? sellable : variants.filter((variant) => variant.price?.amount);
      if (!options.length) return;
      const lowest = options.reduce((current, variant) => Number(variant.price.amount) < Number(current.price.amount) ? variant : current);
      const hasPriceRange = new Set(options.map((variant) => `${variant.price.amount}-${variant.price.currencyCode}`)).size > 1;
      const isConfigurableFrame = document.querySelector(`[data-product-card][data-category="frame"][data-product-handle="${CSS.escape(handle)}"][data-frame-type="gblock"]`);
      const isTurf = document.querySelector(`[data-product-card][data-category="turf"][data-product-handle="${CSS.escape(handle)}"]`);
      setProductCardPrice(handle, lowest.price, Boolean(isConfigurableFrame) || Boolean(isTurf) || hasPriceRange);
    } catch (error) { /* The selection flow still fetches the precise SKU price when a card is selected. */ }
  }));
}
function setProductMessage(message, canAdd) {
  const summary = document.getElementById('product-summary');
  summary.hidden = !validConfiguration();
  const status = document.getElementById('product-status');
  status.textContent = message;
  status.hidden = !message;
  document.getElementById('add-to-cart').disabled = !canAdd;
}
function variantLookupKey() {
  if (!selectedFrameProduct?.handle || !state.selectedGBlock) return '';
  return isGCurve()
    ? `${selectedFrameProduct.handle}|gcurve`
    : `${selectedFrameProduct.handle}|${state.layer}|${state.frameWidth}|${state.frameHeight}`;
}
async function lookupVariant() {
  const summary = document.getElementById('product-summary');
  const lookupKey = variantLookupKey();
  if (!validConfiguration()) { selectedVariant = null; resolvedVariantKey = ''; summary.hidden = true; syncTotal(); return; }
  if (selectedVariant && resolvedVariantKey === lookupKey) return;
  summary.hidden = false;
  if (!selectedFrameProduct?.handle || !storefrontToken) { setProductMessage('Select a frame product and enter the Public Storefront API token in this section settings.', false); return; }
  const requestId = ++lookupId;
  setProductMessage('Checking the selected SKU...', false);
  const query = isGCurve()
    ? 'query FixedFrame($handle:String!){product(handle:$handle){title featuredImage{url} variants(first:20){nodes{id sku availableForSale price{amount currencyCode}}}}}'
    : 'query PlannerVariant($handle:String!,$selectedOptions:[SelectedOptionInput!]!){product(handle:$handle){title featuredImage{url} variantBySelectedOptions(selectedOptions:$selectedOptions,caseInsensitiveMatch:true){id sku availableForSale price{amount currencyCode}}}}';
  try {
    const root = window.Shopify?.routes?.root || '/';
    const variables = isGCurve() ? { handle: selectedFrameProduct.handle } : { handle: selectedFrameProduct.handle, selectedOptions: [{ name: 'Layer', value: state.layer }, { name: 'Width', value: state.frameWidth + ' cm' }, { name: 'Height', value: state.frameHeight + ' cm' }] };
    const response = await fetch(root + 'api/2026-07/graphql.json', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': storefrontToken }, body: JSON.stringify({ query, variables }) });
    const result = await response.json();
    if (requestId !== lookupId) return;
    const product = result.data?.product;
    if (product?.featuredImage?.url) selectedFrameProduct.image = product.featuredImage.url;
    renderSelectedFrame();
    const variant = isGCurve() ? product?.variants?.nodes?.find((item) => item.availableForSale) || product?.variants?.nodes?.[0] : product?.variantBySelectedOptions;
    if (!variant) { resolvedVariantKey = ''; setProductMessage('No Shopify SKU exists for this configuration.', false); return; }
    selectedVariant = variant;
    resolvedVariantKey = lookupKey;
    setProductCardPrice(selectedFrameProduct.handle, variant.price);
    setSelectedPrice('selected-frame-price', variant.price);
    document.getElementById('product-title').textContent = product.title;
    document.getElementById('product-configuration').textContent = `${state.layer} · ${frameDepthText()} D`;
    document.getElementById('product-imperial').textContent = `${cmText(state.frameWidth)} W × ${cmText(state.frameHeight)} H`;
    document.getElementById('product-configuration').textContent = `${state.layer} \u00b7 ${frameDepthText()} D`;
    document.getElementById('product-imperial').textContent = `${cmText(state.frameWidth)} W \u00d7 ${cmText(state.frameHeight)} H`;
    document.getElementById('product-configuration').textContent = isGCurve() ? `G-Curve · ${GCURVE.depth} cm D × ${GCURVE.width} cm W × ${GCURVE.height} cm H` : `${state.layer} · ${frameDepth()} cm D × ${state.frameWidth} cm W × ${state.frameHeight} cm H`;
    document.getElementById('product-imperial').textContent = isGCurve() ? `${(GCURVE.depth / 2.54).toFixed(1)} in D × ${(GCURVE.width / 2.54).toFixed(1)} in W × ${(GCURVE.height / 2.54).toFixed(1)} in H` : `${frameDepthImperial()} D × ${(state.frameWidth / 2.54).toFixed(1)} in W × ${(state.frameHeight / 2.54).toFixed(1)} in H`;
    const skuLine = document.getElementById('product-sku');
    skuLine.textContent = variant.sku ? 'SKU: ' + variant.sku : '';
    skuLine.hidden = !variant.sku;
    document.getElementById('product-price').textContent = money(variant.price.amount, variant.price.currencyCode);
    setProductMessage(variant.availableForSale ? '' : 'This SKU is currently unavailable.', variant.availableForSale);
    syncTotal();
  } catch (error) { if (requestId === lookupId) { resolvedVariantKey = ''; setProductMessage('Unable to check this SKU. Confirm the public token and product publication.', false); syncTotal(); } }
}
function scheduleLookup() {
  clearTimeout(lookupTimer);
  const lookupKey = variantLookupKey();
  if (!validConfiguration()) {
    selectedVariant = null;
    resolvedVariantKey = '';
    syncTotal();
    return;
  }
  if (selectedVariant && resolvedVariantKey === lookupKey) return;
  lookupTimer = setTimeout(lookupVariant, 220);
}
// Use Dawn's own cart-add response to refresh its cart drawer. A separate
// follow-up section request can return stale or incomplete line-item markup.
function plannerDrawerSections(drawer) {
  const sections = drawer?.getSectionsToRender?.()
    ?.map((section) => section.id)
    .filter(Boolean);
  return sections?.length ? [...new Set(sections)] : ['cart-drawer', 'cart-icon-bubble'];
}

async function addPlannerCartItems(root, items) {
  const drawer = document.querySelector('cart-drawer');
  // Match Dawn's native product-form request format exactly. The active theme
  // returns incomplete cart-drawer line markup when these are sent as JSON.
  const payload = new FormData();
  items.forEach((item, index) => {
    payload.append(`items[${index}][id]`, item.id);
    payload.append(`items[${index}][quantity]`, item.quantity || 1);
    Object.entries(item.properties || {}).forEach(([key, value]) => {
      payload.append(`items[${index}][properties][${key}]`, value);
    });
  });
  if (drawer?.renderContents) {
    payload.append('sections', plannerDrawerSections(drawer).join(','));
    payload.append('sections_url', window.location.pathname);
  }
  const response = await fetch(root + 'cart/add.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: payload
  });
  const result = await response.json();
  if (!response.ok || result.status) throw new Error(result.description || 'Cart add failed');

  const drawerMarkup = result.sections?.['cart-drawer'] || '';
  const hasLineItemMarkup = /CartDrawer-Item-|class=["'][^"']*cart-item/.test(drawerMarkup);
  if (drawer?.renderContents && result.sections && hasLineItemMarkup) {
    drawer.renderContents(result);
    drawer.open?.();
    scheduleNativeCartCleanup();
    return result;
  }

  // Never replace the normal Dawn drawer with an empty line-item area.
  window.location.assign(root + 'cart');
  return result;
}

function hideNativeFrameVariantOptions() {
  const selectors = [
    'cart-drawer .cart-item',
    '#CartDrawer .cart-item',
    'cart-items .cart-item',
    '.cart-items .cart-item'
  ].join(', ');
  document.querySelectorAll(selectors).forEach((item) => {
    const options = [...item.querySelectorAll('.product-option')];
    const labels = options.map((option) => option.querySelector('dt')?.textContent.replace(/:/g, '').trim().toLowerCase() || '');
    const isPlannerFrame = labels.some((label) => label === 'planner layer');
    if (!isPlannerFrame) return;
    options.forEach((option) => {
      const term = option.querySelector('dt');
      const label = term?.textContent.replace(/:/g, '').trim().toLowerCase();
      // Shopify's variant option labels duplicate the three planner properties.
      // Keep the planner properties because they use the customer's selected size.
      if (['layer', 'width', 'height'].includes(label)) {
        option.hidden = true;
        option.style.display = 'none';
        return;
      }
      if (label === 'planner layer') term.textContent = 'Layer:';
      if (label === 'planner width') term.textContent = 'Width:';
      if (label === 'planner height') term.textContent = 'Height:';
    });
  });
}

function scheduleNativeCartCleanup() {
  requestAnimationFrame(() => requestAnimationFrame(hideNativeFrameVariantOptions));
}

function frameCartProperties() {
  if (isGCurve()) {
    return {
      'Planner Layer': 'Fixed size',
      'Planner Width': cartDimensionText(GCURVE.width),
      'Planner Height': cartDimensionText(GCURVE.height)
    };
  }
  return {
    'Planner Layer': state.layer,
    'Planner Width': cartDimensionText(state.frameWidth),
    'Planner Height': cartDimensionText(state.frameHeight)
  };
}

async function addSelectedVariant() {
  if (!plannerCheckoutEnabled) return;
  if (!selectedVariant) return;
  const button = document.getElementById('add-to-cart'); button.disabled = true; button.textContent = 'Adding...';
  try {
    const root = window.Shopify?.routes?.root || '/';
    const id = selectedVariant.id.split('/').pop();
    const properties = frameCartProperties();
    await addPlannerCartItems(root, [{ id, quantity: 1, properties }]);
  } catch (error) { setProductMessage('Could not add this SKU to cart. Please try again.', false); }
  finally { button.textContent = 'Add to cart'; button.disabled = !(selectedVariant && selectedVariant.availableForSale); }
}

function setAccessoryImage(id, url, alt) {
  const slot = document.getElementById(id);
  slot.replaceChildren();
  if (url) { const image = document.createElement('img'); image.src = url; image.alt = alt || ''; image.width = 240; image.height = 240; slot.append(image); }
  else slot.textContent = 'G';
}

function renderSelectedFrame() {
  const holder = document.getElementById('selected-frame');
  if (!state.selectedGBlock || !selectedFrameProduct) {
    holder.hidden = true;
    return;
  }
  const card = cardsForCategory('frame').find((item) => item.dataset.productHandle === selectedFrameProduct.handle);
  const fallbackImage = card?.dataset.productImage || '';
  const fallbackTitle = card?.dataset.productTitle || 'G-Block';
  const image = selectedFrameProduct.image || fallbackImage;
  if (image) selectedFrameProduct.image = image;
  setAccessoryImage('selected-frame-image', image, '');
  const name = document.getElementById('selected-frame-name');
  name.textContent = selectedFrameProduct.title || fallbackTitle;
  name.href = selectedFrameProduct.url || '#';
  holder.hidden = false;
}
function currentAccessory() { return ACCESSORIES[state.step]; }
function cardsForCategory(category) { return [...document.querySelectorAll(`[data-product-card][data-category="${category}"]`)]; }
function syncAccessoryUI() {
  const accessory = currentAccessory();
  if (!accessory) return;
  const selection = state.accessories[state.step];
  const productTitle = selection.title || accessory.name;
  const productImage = selection.image || '';
  const cards = cardsForCategory(state.step);
  cards.forEach((card) => {
    const active = card.dataset.productHandle === selection.handle;
    const incompatible = state.step === 'projector' && isGCurve() && !/4k/i.test(card.dataset.productTitle || '');
    card.hidden = Boolean(selection.selected);
    card.querySelectorAll('[data-product-select]').forEach((control) => { control.disabled = incompatible; });
    card.classList.toggle('is-disabled', incompatible);
    card.classList.toggle('is-active', active);
    card.querySelectorAll('[data-product-select]').forEach((control) => { control.setAttribute('aria-pressed', String(active)); });
    let note = card.querySelector('.gebblo-planner__product-requirement');
    if (!note && incompatible) { note = document.createElement('span'); note.className = 'gebblo-planner__product-requirement'; card.append(note); }
    if (note) { note.hidden = !incompatible; note.textContent = incompatible ? 'Not compatible with G-Curve — 4K projector required.' : ''; }
  });
  document.getElementById('accessory-empty').hidden = cards.length > 0 || Boolean(selection.selected);
  document.getElementById('accessory-selected').hidden = !selection.selected;
  const selectedName = document.getElementById('accessory-selected-name');
  selectedName.textContent = productTitle;
  selectedName.href = selection.url || '#';
  setSelectedPrice('accessory-selected-price', selection.variant?.price);
  document.getElementById('accessory-change').hidden = !selection.selected;
  if (selection.selected) setAccessoryImage('accessory-selected-image', productImage, '');
  renderProjectorGuidance();
  renderTurfRollOptions();
  syncTotal();
}
async function lookupAccessory(step) {
  const accessory = ACCESSORIES[step]; const selection = state.accessories[step];
  if (!selection?.handle || !storefrontToken) { selection.status = 'This product will be available soon.'; syncAccessoryUI(); return; }
  selection.status = 'Checking availability...'; syncAccessoryUI();
  const query = 'query Accessory($handle:String!){product(handle:$handle){title featuredImage{url altText} variants(first:20){nodes{id sku title availableForSale price{amount currencyCode}}}}}';
  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(root + 'api/2026-07/graphql.json', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': storefrontToken }, body: JSON.stringify({ query, variables: { handle: selection.handle } }) });
    const product = (await response.json()).data?.product;
    if (!product) throw new Error('Product not found');
    selection.title = product.title; selection.image = product.featuredImage?.url || '';
    const savedVariantId = selection.variant?.id;
    selection.variants = product.variants.nodes;
    const savedVariant = selection.variants.find((item) => item.id === savedVariantId && item.availableForSale);
    const variant = step === 'turf'
      ? savedVariant || preferredTurfVariant(selection.variants)
      : savedVariant || selection.variants.find((item) => item.availableForSale) || selection.variants[0];
    selection.variant = variant || null; selection.status = variant ? (variant.availableForSale ? '' : 'This product is currently unavailable.') : 'No Shopify SKU is available for this product.';
    if (variant?.price) setProductCardPrice(selection.handle, variant.price);
  } catch (error) { selection.status = 'Unable to check this product right now.'; selection.variant = null; }
  if (step === 'turf') updateRoom();
  syncAccessoryUI();
}
async function reviewConfiguration() {
  if (!plannerCheckoutEnabled) return;
  const button = document.getElementById('review-add');
  const status = document.getElementById('review-status');
  button.disabled = true; button.textContent = 'Adding to cart...';
  status.hidden = true;
  try {
    if (state.selectedGBlock && !selectedVariant) await lookupVariant();
    for (const step of ['turf', 'mat', 'monitor', 'projector']) {
      const selection = state.accessories[step];
      if (selection.selected && !selection.variant) await lookupAccessory(step);
    }
    const items = [];
    if (selectedVariant?.availableForSale) {
      const properties = frameCartProperties();
      items.push({ id: selectedVariant.id.split('/').pop(), quantity: 1, properties });
    }
    ['turf', 'mat', 'monitor', 'projector'].forEach((step) => { const selection = state.accessories[step]; if (selection.selected && selection.variant?.availableForSale) items.push({ id: selection.variant.id.split('/').pop(), quantity: 1 }); });
    if (!items.length) throw new Error('No available items');
    const root = window.Shopify?.routes?.root || '/';
    await addPlannerCartItems(root, items);
    button.textContent = 'Added to cart';
  } catch (error) {
    status.textContent = 'Please make sure each selected product has an available Shopify SKU before adding to cart.';
    status.hidden = false;
    button.disabled = false;
    button.innerHTML = 'Add to cart <span aria-hidden="true">&rarr;</span>';
  }
}

function syncUI() {
  syncFrameRangeLimits();
  ['width', 'height', 'depth', 'frameWidth', 'frameHeight'].forEach((key) => {
    const input = rangeValue(key);
    if (!input) return;
    input.value = state[key];
    document.getElementById(`${hyphenate(key)}-value`).textContent = cmText(state[key]);
  });
  document.getElementById('status-copy').innerHTML = (state.step === 'room' || state.step === 'review')
    ? '<span>Depth <b>' + cmText(state.depth) + '</b></span><span>Width <b>' + cmText(state.width) + '</b></span><span>Height <b>' + cmText(state.height) + '</b></span>'
    : state.step === 'frame' && state.selectedGBlock
      ? (isGCurve() ? '<span>Depth <b>' + cmText(GCURVE.depth) + '</b></span><span>Width <b>' + cmText(GCURVE.width) + '</b></span><span>Height <b>' + cmText(GCURVE.height) + '</b></span>' : '<span>Depth <b>' + frameDepthText() + '</b></span><span>Width <b>' + cmText(state.frameWidth) + '</b></span><span>Height <b>' + cmText(state.frameHeight) + '</b></span>')
      : state.step === 'frame' ? '<span>Select a frame</span>' : '<span>Optional add-on</span>';
  document.querySelectorAll('[data-step-jump]').forEach((button) => button.classList.toggle('is-active', button.dataset.stepJump === state.step));
  const frameCards = cardsForCategory('frame');
  const roomNotice = document.getElementById('frame-fit-notice');
  roomNotice.hidden = !roomFitNotice;
  roomNotice.textContent = roomFitNotice;
  frameCards.forEach((card) => {
    const active = card.dataset.productHandle === selectedFrameProduct?.handle;
    const requirements = card.dataset.frameType === 'gcurve' ? gCurveRequirement() : [];
    card.hidden = state.selectedGBlock;
    card.querySelectorAll('[data-product-select]').forEach((control) => { control.disabled = requirements.length > 0; });
    card.classList.toggle('is-disabled', requirements.length > 0);
    card.classList.toggle('is-active', active);
    card.querySelectorAll('[data-product-select]').forEach((control) => { control.setAttribute('aria-pressed', String(active)); });
    const copy = card.querySelector('.gebblo-planner__product-requirement');
    if (copy) { copy.hidden = requirements.length === 0; copy.textContent = requirements.join(' '); }
  });
  document.getElementById('frame-empty').hidden = frameCards.length > 0;
  if (state.step !== 'frame') frameCards.forEach((card) => { card.hidden = true; });
  document.querySelectorAll('[data-layer]').forEach((button) => button.classList.toggle('is-active', button.dataset.layer === state.layer));
  document.getElementById('layer-options').hidden = !state.selectedGBlock || isGCurve();
  document.getElementById('frame-size').hidden = !state.selectedGBlock || isGCurve();
  renderSelectedFrame();
  document.getElementById('change-frame').hidden = !state.selectedGBlock;
  renderFit();
  renderProjectorGuidance();
  scheduleLookup();
  syncAccessoryUI();
  syncTotal();
}

function change(key, value) {
  const input = rangeValue(key);
  state[key] = Math.min(Number(input.max), Math.max(Number(input.min), value));
  const gCurveRemoved = ['width', 'height', 'depth'].includes(key) && removeIncompatibleGCurve();
  syncUI(); updateRoom();
  if (gCurveRemoved) {
    clearTimeout(roomFitNoticeTimer);
    roomFitNoticeTimer = window.setTimeout(() => { roomFitNotice = ''; syncUI(); }, 4200);
  }
}

function setStep(step) {
  if (step !== 'review') state.lastConfigurationStep = step;
  state.step = step;
  const index = STEPS.indexOf(step); const roomStep = step === 'room'; const frameStep = step === 'frame'; const accessoryStep = index >= 2 && index <= 5;
  const labels = { room: 'Room dimensions', frame: 'Simulator enclosure', turf: 'Turf', mat: 'Hitting mat', monitor: 'Launch monitor', projector: 'Projector', review: 'Configuration summary' };
  document.getElementById('room-panel').hidden = !roomStep;
  document.getElementById('frame-panel').hidden = !frameStep;
  document.getElementById('accessory-panel').hidden = !accessoryStep;
  document.getElementById('review-panel').hidden = step !== 'review';
  document.getElementById('step-number').textContent = String(index + 1).padStart(2, '0');
  document.getElementById('panel-title').textContent = labels[step];
  const next = STEPS[index + 1];
  const actions = document.querySelector('.gebblo-planner__actions');
  document.getElementById('continue-step').innerHTML = next ? (next === 'review' ? 'Confirm <span aria-hidden="true">&rarr;</span>' : `Continue <span aria-hidden="true">&rarr;</span>`) : '';
  document.getElementById('continue-step').hidden = !next;
  document.getElementById('back-step').hidden = false;
  document.getElementById('back-step').textContent = roomStep ? 'Back to overview' : 'Back';
  actions.hidden = step === 'review';
  syncUI(); updateRoom(roomStep); requestAnimationFrame(resize);
  if (accessoryStep) {
    cardsForCategory('turf').forEach((card) => { card.hidden = step !== 'turf' || Boolean(state.accessories.turf.selected); });
    cardsForCategory('mat').forEach((card) => { card.hidden = step !== 'mat' || Boolean(state.accessories.mat.selected); });
    cardsForCategory('monitor').forEach((card) => { card.hidden = step !== 'monitor' || Boolean(state.accessories.monitor.selected); });
    cardsForCategory('projector').forEach((card) => { card.hidden = step !== 'projector' || Boolean(state.accessories.projector.selected); });
    const visibleCards = cardsForCategory(step).filter((card) => !card.hidden);
    const empty = document.getElementById('accessory-empty');
    empty.hidden = visibleCards.length > 0 || Boolean(state.accessories[step]?.selected);
    empty.textContent = 'No products have been added for this category yet.';
  }
  if (step === 'review') {
    const reviewButton = document.getElementById('review-add');
    reviewButton.hidden = !plannerCheckoutEnabled;
    reviewButton.disabled = !plannerCheckoutEnabled;
    if (plannerCheckoutEnabled) reviewButton.innerHTML = 'Add to cart <span aria-hidden="true">&rarr;</span>';
    document.getElementById('review-status').hidden = true;
    renderReview();
  }
}

// On phones the 3D preview stays sticky above the controls. After moving
// forward, return the customer to the new step title just below that preview.
function scrollToActiveStepTitle() {
  if (!window.matchMedia('(max-width: 900px)').matches) return;
  const title = document.querySelector('.gebblo-planner__heading');
  window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
    title?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }));
}

['width', 'height', 'depth', 'frameWidth', 'frameHeight'].forEach((key) => {
  const input = rangeValue(key);
  input.addEventListener('input', () => change(key, Number(input.value)));
  const hyphenated = hyphenate(key);
  document.getElementById(`${hyphenated}-plus`).addEventListener('click', () => change(key, state[key] + Number(input.step)));
  document.getElementById(`${hyphenated}-minus`).addEventListener('click', () => change(key, state[key] - Number(input.step)));
});

document.getElementById('continue-step').addEventListener('click', () => { const next = STEPS[STEPS.indexOf(state.step) + 1]; if (next) { setStep(next); scrollToActiveStepTitle(); } });
document.getElementById('back-step').addEventListener('click', () => {
  if (state.step === 'room') { returnToSetup(); return; }
  const previous = STEPS[STEPS.indexOf(state.step) - 1];
  if (previous) setStep(previous);
});
document.querySelectorAll('[data-layer]').forEach((button) => {
  button.addEventListener('click', () => { state.layer = button.dataset.layer; state.selectedGBlock = true; syncUI(); updateRoom(); });
});
cardsForCategory('frame').forEach((card) => card.querySelectorAll('[data-product-select]').forEach((control) => control.addEventListener('click', () => {
  if (control.disabled) return;
  selectedFrameProduct = { handle: card.dataset.productHandle, title: card.dataset.productTitle, image: card.dataset.productImage, url: card.dataset.productUrl, type: card.dataset.frameType || 'gblock' };
  roomFitNotice = '';
  if (state.accessories.turf?.selected) {
    if (isGCurve()) state.accessories.turf.turfRollManuallySelected = false;
    applyDefaultTurfVariant();
  }
  if (isGCurve() && state.accessories.projector?.selected && !/4k/i.test(state.accessories.projector.title || '')) state.accessories.projector = {};
  state.selectedGBlock = true; state.layer = 'Single Layer'; selectedVariant = null; resolvedVariantKey = ''; setSelectedPrice('selected-frame-price', null);
  renderSelectedFrame();
  syncUI(); updateRoom();
})));
document.getElementById('change-frame').addEventListener('click', () => { state.layer = null; state.selectedGBlock = false; selectedFrameProduct = null; selectedVariant = null; resolvedVariantKey = ''; setSelectedPrice('selected-frame-price', null); syncUI(); updateRoom(); loadProductCardPrices(); });
document.querySelectorAll('[data-product-card]:not([data-category="frame"])').forEach((card) => card.querySelectorAll('[data-product-select]').forEach((control) => control.addEventListener('click', () => {
  if (control.disabled) return;
  const step = card.dataset.category; const previous = state.accessories[step] || {};
  state.accessories[step] = { selected: true, handle: card.dataset.productHandle, title: card.dataset.productTitle, image: card.dataset.productImage, url: card.dataset.productUrl, variant: null, variants: [], turfRollManuallySelected: false };
  if (step === 'projector') { state.projectorDistance = null; state.projectorLensHeight = null; state.projectorMount = ceilingMountAllowed() ? 'center' : 'side'; }
  lookupAccessory(step); syncAccessoryUI(); updateRoom();
})));
document.getElementById('accessory-change').addEventListener('click', () => { state.accessories[state.step] = {}; syncAccessoryUI(); updateRoom(); loadProductCardPrices(); });
document.getElementById('turf-roll-choice').addEventListener('click', (event) => {
  const button = event.target.closest('[data-turf-variant-id]');
  const selection = state.accessories.turf;
  if (!button || button.disabled || !selection?.selected) return;
  const variant = selection.variants?.find((item) => item.id === button.dataset.turfVariantId);
  if (!variant) return;
  selection.variant = variant;
  selection.turfRollManuallySelected = true;
  selection.status = '';
  syncAccessoryUI();
  updateRoom();
});
document.getElementById('projector-distance-range').addEventListener('input', (event) => {
  state.projectorDistance = Number(event.target.value);
  renderProjectorGuidance();
  updateRoom();
});
document.getElementById('projector-height-range').addEventListener('input', (event) => {
  state.projectorLensHeight = Number(event.target.value);
  renderProjectorGuidance();
  updateRoom();
});
document.querySelectorAll('[data-projector-mount]').forEach((button) => button.addEventListener('click', () => {
  if (button.disabled) return;
  state.projectorMount = button.dataset.projectorMount;
  state.projectorLensHeight = null;
  renderProjectorGuidance();
  updateRoom();
}));
document.getElementById('review-back').addEventListener('click', () => setStep(state.lastConfigurationStep));
document.getElementById('review-add').addEventListener('click', reviewConfiguration);
document.querySelectorAll('[data-step-jump]').forEach((button) => button.addEventListener('click', () => setStep(button.dataset.stepJump)));
document.getElementById('add-to-cart').addEventListener('click', addSelectedVariant);

function resize() {
  const { width, height } = mount.getBoundingClientRect();
  if (width < 1 || height < 1) return;
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
  renderer.setSize(width, height, false);
}

window.addEventListener('resize', resize);
const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(resize) : null;
resizeObserver?.observe(mount);
restorePlannerState();
resize();
setStep('room');
['turf', 'mat', 'monitor', 'projector'].forEach((step) => { if (state.accessories[step]?.selected) lookupAccessory(step); });
loadProductCardPrices();
scheduleNativeCartCleanup();

/* =========================
   01 Setup Entry
========================= */
const setupEntry = document.getElementById('setup-entry');
const plannerShell = document.getElementById('planner-shell');
const setupContinue = document.getElementById('setup-continue');

function openPlannerFromSetup(event) {
  event?.preventDefault();
  if (!setupEntry || !plannerShell) return;
  setupEntry.hidden = true;
  plannerShell.hidden = false;
  plannerShell.classList.remove('gebblo-planner__shell--entry-hidden');
  requestAnimationFrame(() => {
    resize();
    window.dispatchEvent(new Event('resize'));
    plannerShell.scrollIntoView({ behavior: 'smooth', block: 'start' });
    document.getElementById('depth-range')?.focus({ preventScroll: true });
  });
}

function returnToSetup() {
  if (!setupEntry || !plannerShell) return;
  plannerShell.hidden = true;
  plannerShell.classList.add('gebblo-planner__shell--entry-hidden');
  setupEntry.hidden = false;
  requestAnimationFrame(() => {
    setupEntry.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setupContinue?.focus({ preventScroll: true });
  });
}

if (setupEntry && plannerShell && setupContinue) {
  setupContinue.addEventListener('click', openPlannerFromSetup);
  document.querySelectorAll('[data-open-planner]').forEach((button) => button.addEventListener('click', openPlannerFromSetup));
} else {
  plannerShell?.removeAttribute('hidden');
  plannerShell?.classList.remove('gebblo-planner__shell--entry-hidden');
}
function animate() { requestAnimationFrame(animate); controls.update(); updateOccludingWalls(); renderer.render(scene, camera); }
animate();
